import { useState, useEffect, useCallback, useMemo } from "react";
import ManuscriptFormatter from "./ManuscriptFormatter";
import AuthorIndexPage from "./AuthorIndexPage";
import AdminScholarPanel from "./AdminScholarPanel";
import {
  parseAuthors,
  getGoogleScholarAuthorUrl,
  getGoogleScholarArticleUrl,
  generateHighwireTags,
  generateScholarlyJsonLd,
} from "./scholarUtils";

/* ─────────────────────────────────────────────────────────────────────────
   SUPABASE CONFIGURATION
   ───────────────────────────────────────────────────────────────────────── */
const SUPABASE_URL =
  process.env.REACT_APP_SUPABASE_URL || "https://beqexighufrwuwrmcepd.supabase.co";
const SUPABASE_ANON_KEY =
  process.env.REACT_APP_SUPABASE_ANON_KEY ||
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJlcWV4aWdodWZyd3V3cm1jZXBkIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzgzMTUzOTQsImV4cCI6MjA5Mzg5MTM5NH0.clBKXryl92XSTofZZzQbe3U-MUGFW0wGw9W5KexOG1g";
const ADMIN_PASS = process.env.REACT_APP_ADMIN_PASS || "admin";

/* ─────────────────────────────────────────────────────────────────────────
   HIGH-PERFORMANCE IN-MEMORY CLIENT CACHE (Stops lagging & network delays)
   ───────────────────────────────────────────────────────────────────────── */
const queryCache = new Map();
const CACHE_TTL_MS = 3 * 60 * 1000; // 3 minutes fresh

function getCached(key) {
  const hit = queryCache.get(key);
  if (!hit) return null;
  if (Date.now() - hit.ts > CACHE_TTL_MS) {
    queryCache.delete(key);
    return null;
  }
  return hit.data;
}

function setCached(key, data) {
  queryCache.set(key, { data, ts: Date.now() });
  if (Array.isArray(data)) {
    data.forEach((item) => {
      if (item?.id) {
        queryCache.set(`article:${item.id}`, { data: item, ts: Date.now() });
      }
    });
  }
}

function invalidateCache() {
  queryCache.clear();
}

/* ─────────────────────────────────────────────────────────────────────────
   PDF UPLOAD HELPER – uploads to Supabase Storage (bucket: "papers")
   Returns a relative path: /papers/filename
   ───────────────────────────────────────────────────────────────────────── */
async function uploadPDF(file, title) {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .slice(0, 80);
  const filename = `${slug}-${Date.now()}.pdf`;

  const res = await fetch(
    `${SUPABASE_URL}/storage/v1/object/papers/${filename}`,
    {
      method: "POST",
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        "Content-Type": file.type,
      },
      body: file,
    }
  );

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "PDF upload failed");
  }

  return `/papers/${filename}`;
}

/* ─────────────────────────────────────────────────────────────────────────
   DATABASE HELPERS & DOI MANAGEMENT
   ───────────────────────────────────────────────────────────────────────── */
function getLocalDoiMap() {
  try {
    return JSON.parse(localStorage.getItem("sx_manuscript_dois") || "{}");
  } catch (e) {
    return {};
  }
}

function saveLocalDoi(id, doi) {
  try {
    const map = getLocalDoiMap();
    if (doi && String(doi).trim()) {
      map[id] = normalizeDoi(doi);
    } else {
      delete map[id];
    }
    localStorage.setItem("sx_manuscript_dois", JSON.stringify(map));
  } catch (e) {}
}

function normalizeDoi(val) {
  if (!val) return "";
  let s = String(val).trim();
  if (!s) return "";
  if (s.startsWith("http://dx.doi.org/")) s = "https://doi.org/" + s.slice(18);
  if (s.startsWith("http://doi.org/")) s = "https://doi.org/" + s.slice(15);
  if (s.startsWith("https://doi.org/")) return s;
  s = s.replace(/^doi:\s*/i, "");
  if (s.startsWith("10.")) return `https://doi.org/${s}`;
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  return `https://doi.org/${s}`;
}

function getDoiDisplay(urlOrDoi) {
  if (!urlOrDoi) return "";
  return String(urlOrDoi).replace(/^https?:\/\/(?:dx\.)?doi\.org\//i, "");
}

function extractDoiFromCoverLetter(coverLetter) {
  if (!coverLetter) return "";
  const match = coverLetter.match(/(?:\[?DOI:\s*([^\s\]\n\r]+)\]?)|(?:https?:\/\/doi\.org\/([^\s\n\r]+))/i);
  if (match) {
    const val = (match[1] || match[2] || "").trim();
    return normalizeDoi(val);
  }
  return "";
}

function getManuscriptDoi(m) {
  if (!m) return "";
  if (m.doi && typeof m.doi === "string" && m.doi.trim()) return normalizeDoi(m.doi);
  if (m.doi_link && typeof m.doi_link === "string" && m.doi_link.trim()) return normalizeDoi(m.doi_link);
  const local = getLocalDoiMap();
  if (m.id && local[m.id]) return normalizeDoi(local[m.id]);
  if (m.cover_letter) {
    const extracted = extractDoiFromCoverLetter(m.cover_letter);
    if (extracted) return extracted;
  }
  return "";
}

function enrichManuscriptWithDoi(m) {
  if (!m) return m;
  const doi = getManuscriptDoi(m);
  return { ...m, doi: doi || m.doi || "" };
}

async function sb(path, opts = {}) {
  const res = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
    headers: {
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      "Content-Type": "application/json",
      Prefer: opts.prefer || "return=representation",
      ...opts.headers,
    },
    ...opts,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || res.statusText);
  }
  const text = await res.text();
  return text ? JSON.parse(text) : null;
}

async function fetchArticles(filters = {}) {
  const cacheKey = `articles:${filters.category || "all"}:${filters.limit || "all"}`;
  const cached = getCached(cacheKey);
  if (cached) {
    return cached.map(enrichManuscriptWithDoi);
  }
  let qs = "status=eq.published&order=published_at.desc";
  if (filters.category) qs += `&category=eq.${encodeURIComponent(filters.category)}`;
  if (filters.limit) qs += `&limit=${filters.limit}`;
  const rows = await sb(`manuscripts?${qs}`);
  const enriched = (rows || []).map(enrichManuscriptWithDoi);
  if (enriched) {
    setCached(cacheKey, enriched);
  }
  return enriched;
}

async function fetchArticle(id) {
  const cacheKey = `article:${id}`;
  const cached = getCached(cacheKey);
  if (cached) {
    sb(`manuscripts?id=eq.${id}`, {
      method: "PATCH",
      prefer: "return=minimal",
      body: JSON.stringify({ views: (cached.views || 0) + 1 }),
    }).catch(() => {});
    return enrichManuscriptWithDoi(cached);
  }
  const rows = await sb(`manuscripts?id=eq.${id}&status=eq.published`);
  if (!rows?.length) return null;
  const article = enrichManuscriptWithDoi(rows[0]);
  setCached(cacheKey, article);
  sb(`manuscripts?id=eq.${id}`, {
    method: "PATCH",
    prefer: "return=minimal",
    body: JSON.stringify({ views: (article.views || 0) + 1 }),
  }).catch(() => {});
  return article;
}

async function insertSubmission(data) {
  invalidateCache();
  return sb("manuscripts", {
    method: "POST",
    body: JSON.stringify({ ...data, status: "pending", views: 0 }),
  });
}

async function fetchAllManuscripts(skipCache = false) {
  const cacheKey = "manuscripts:all";
  if (!skipCache) {
    const cached = getCached(cacheKey);
    if (cached) return cached.map(enrichManuscriptWithDoi);
  }
  const rows = await sb("manuscripts?order=submitted_at.desc");
  const enriched = (rows || []).map(enrichManuscriptWithDoi);
  if (enriched) {
    setCached(cacheKey, enriched);
  }
  return enriched;
}

async function updateManuscriptStatus(id, status, extra = {}) {
  invalidateCache();
  const { doi, ...restExtra } = extra;
  if (doi !== undefined) {
    saveLocalDoi(id, doi);
  }
  try {
    const res = await sb(`manuscripts?id=eq.${id}`, {
      method: "PATCH",
      prefer: "return=representation",
      body: JSON.stringify({ status, ...extra }),
    });
    return Array.isArray(res) ? res.map(enrichManuscriptWithDoi) : res;
  } catch (err) {
    if (err.message && (err.message.includes("doi") || err.message.includes("schema cache") || err.message.includes("does not exist"))) {
      let coverLetterUpdate = {};
      if (doi && String(doi).trim()) {
        try {
          const current = await sb(`manuscripts?id=eq.${id}&select=cover_letter`);
          const existingCover = (current && current[0] && current[0].cover_letter) || "";
          const cleanedCover = existingCover.replace(/\[?DOI:\s*[^\]\n\r]+\]?/gi, "").trim();
          const normalized = normalizeDoi(doi);
          const newCover = cleanedCover ? `${cleanedCover}\n\n[DOI: ${normalized}]` : `[DOI: ${normalized}]`;
          coverLetterUpdate = { cover_letter: newCover };
        } catch (_) {}
      }
      const res = await sb(`manuscripts?id=eq.${id}`, {
        method: "PATCH",
        prefer: "return=representation",
        body: JSON.stringify({ status, ...restExtra, ...coverLetterUpdate }),
      });
      return Array.isArray(res) ? res.map(enrichManuscriptWithDoi) : res;
    }
    throw err;
  }
}

async function updateManuscript(id, updates) {
  invalidateCache();
  const { doi, ...restUpdates } = updates;
  if (doi !== undefined) {
    saveLocalDoi(id, doi);
  }
  try {
    const res = await sb(`manuscripts?id=eq.${id}`, {
      method: "PATCH",
      prefer: "return=representation",
      body: JSON.stringify(updates),
    });
    return Array.isArray(res) ? res.map(enrichManuscriptWithDoi) : res;
  } catch (err) {
    if (err.message && (err.message.includes("doi") || err.message.includes("schema cache") || err.message.includes("does not exist"))) {
      let coverLetter = restUpdates.cover_letter || "";
      if (doi && String(doi).trim()) {
        const cleanedCover = coverLetter.replace(/\[?DOI:\s*[^\]\n\r]+\]?/gi, "").trim();
        const normalized = normalizeDoi(doi);
        coverLetter = cleanedCover ? `${cleanedCover}\n\n[DOI: ${normalized}]` : `[DOI: ${normalized}]`;
      }
      const res = await sb(`manuscripts?id=eq.${id}`, {
        method: "PATCH",
        prefer: "return=representation",
        body: JSON.stringify({ ...restUpdates, cover_letter: coverLetter }),
      });
      return Array.isArray(res) ? res.map(enrichManuscriptWithDoi) : res;
    }
    throw err;
  }
}

async function deleteManuscript(id) {
  invalidateCache();
  return sb(`manuscripts?id=eq.${id}`, { method: "DELETE", prefer: "return=minimal" });
}

const CATEGORY_LIST = [
  "Social Sciences",
  "Health & Medicine",
  "Natural Sciences",
  "Technology & Engineering",
  "Humanities",
  "Education",
  "Law & Policy",
  "Economics",
];

const CATEGORY_COLORS = {
  "Social Sciences": "#2563EB",
  "Health & Medicine": "#059669",
  "Natural Sciences": "#7C3AED",
  "Technology & Engineering": "#D97706",
  "Humanities": "#DC2626",
  "Education": "#0891B2",
  "Law & Policy": "#4B5563",
  "Economics": "#B45309",
};

const STATUS_META = {
  pending: { bg: "#FEF9C3", fg: "#713F12", label: "Under Review" },
  published: { bg: "#DCFCE7", fg: "#14532D", label: "Published" },
  rejected: { bg: "#FEE2E2", fg: "#7F1D1D", label: "Rejected" },
  revision: { bg: "#E0F2FE", fg: "#0C4A6E", label: "Revision Requested" },
};

function formatDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? null : d;
}

/* ─────────────────────────────────────────────────────────────────────────
   GLOBAL STYLES
   ───────────────────────────────────────────────────────────────────────── */
const GlobalStyles = () => (
  <style>{`
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    :root {
      --scarlet: #C0392B;
      --scarlet-light: #E74C3C;
      --ink: #0F0F0F;
      --ink-70: rgba(15,15,15,0.7);
      --ink-40: rgba(15,15,15,0.4);
      --ink-15: rgba(15,15,15,0.15);
      --ink-07: rgba(15,15,15,0.07);
      --cream: #FAFAF7;
      --white: #FFFFFF;
      --serif-display: 'Playfair Display', Georgia, serif;
      --serif-body: 'Source Serif 4', Georgia, serif;
      --mono: 'JetBrains Mono', monospace;
    }

    html { scroll-behavior: smooth; }

    body {
      background: var(--cream);
      color: var(--ink);
      font-family: var(--serif-body);
      font-size: 16px;
      line-height: 1.6;
      -webkit-font-smoothing: antialiased;
    }

    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(18px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes fadeIn {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes shimmer {
      0%   { background-position: -400px 0; }
      100% { background-position: 400px 0; }
    }
    @keyframes spinDot {
      to { transform: rotate(360deg); }
    }
    @keyframes marqueeScroll {
      0%   { transform: translateX(0); }
      100% { transform: translateX(-50%); }
    }

    .animate-up { animation: fadeUp 0.55s cubic-bezier(0.22,1,0.36,1) both; }
    .animate-up-d1 { animation-delay: 0.08s; }
    .animate-up-d2 { animation-delay: 0.16s; }
    .animate-up-d3 { animation-delay: 0.24s; }
    .animate-up-d4 { animation-delay: 0.32s; }
    .animate-fade { animation: fadeIn 0.4s ease both; }

    .container { width: 100%; max-width: 1120px; margin: 0 auto; padding: 0 24px; }
    .container-narrow { max-width: 760px; }

    .display-xl { font-family: var(--serif-display); font-size: clamp(42px, 7vw, 80px); font-weight: 900; line-height: 1.05; letter-spacing: -0.02em; }
    .display-lg { font-family: var(--serif-display); font-size: clamp(28px, 4vw, 44px); font-weight: 700; line-height: 1.15; letter-spacing: -0.01em; }
    .display-md { font-family: var(--serif-display); font-size: clamp(20px, 3vw, 28px); font-weight: 600; line-height: 1.25; }
    .label { font-family: var(--mono); font-size: 10px; font-weight: 500; letter-spacing: 0.12em; text-transform: uppercase; }

    .btn {
      display: inline-flex; align-items: center; justify-content: center; gap: 8px;
      padding: 12px 24px; font-family: var(--mono); font-size: 11px; font-weight: 500;
      letter-spacing: 0.1em; text-transform: uppercase; text-decoration: none;
      border: 1.5px solid var(--ink); background: transparent; color: var(--ink);
      cursor: pointer; transition: background 0.2s, color 0.2s, transform 0.15s, box-shadow 0.2s;
      white-space: nowrap;
    }
    .btn:hover { background: var(--ink); color: var(--white); transform: translateY(-1px); box-shadow: 0 4px 16px rgba(0,0,0,0.15); }
    .btn-primary { background: var(--scarlet); color: white; border-color: var(--scarlet); }
    .btn-primary:hover { background: #a93226; }
    .btn-ghost { border-color: var(--ink-15); color: var(--ink-70); }
    .btn-ghost:hover { background: var(--ink); color: white; }
    .btn-sm { padding: 8px 16px; font-size: 10px; }

    .skeleton {
      background: linear-gradient(90deg, #E8E8E4 25%, #F4F4F0 50%, #E8E8E4 75%);
      background-size: 400px 100%;
      animation: shimmer 1.4s ease infinite;
      border-radius: 2px;
    }

    .card { background: white; border: 1px solid var(--ink-15); transition: box-shadow 0.25s, transform 0.25s; }
    .card:hover { box-shadow: 0 8px 32px rgba(0,0,0,0.1); transform: translateY(-2px); }

    .field {
      width: 100%; padding: 14px 16px;
      border: 1.5px solid var(--ink-15);
      font-family: var(--serif-body); font-size: 15px; color: var(--ink);
      background: white; transition: border-color 0.2s, box-shadow 0.2s;
      outline: none;
    }
    .field:focus { border-color: var(--scarlet); box-shadow: 0 0 0 3px rgba(192,57,43,0.1); }
    .field-label { font-family: var(--mono); font-size: 10px; font-weight: 500; letter-spacing: 0.1em; text-transform: uppercase; color: var(--ink-70); display: block; margin-bottom: 6px; }

    .rule { height: 1px; background: var(--ink-15); }
    .rule-heavy { height: 3px; background: var(--ink); }

    .cat-pill {
      display: inline-block; padding: 2px 10px;
      font-family: var(--mono); font-size: 9px; font-weight: 500; letter-spacing: 0.1em; text-transform: uppercase;
      border-radius: 100px;
    }

    .nav-item {
      font-family: var(--mono); font-size: 10.5px; font-weight: 500; letter-spacing: 0.1em; text-transform: uppercase;
      color: var(--ink-70); cursor: pointer; background: none; border: none;
      padding: 6px 0; position: relative; transition: color 0.2s;
    }
    .nav-item::after {
      content: ''; position: absolute; bottom: -2px; left: 0; right: 0;
      height: 1.5px; background: var(--scarlet); transform: scaleX(0); transition: transform 0.2s;
    }
    .nav-item:hover { color: var(--ink); }
    .nav-item:hover::after { transform: scaleX(1); }
    .nav-item.active { color: var(--scarlet); }
    .nav-item.active::after { transform: scaleX(1); }

    .marquee-wrapper {
      overflow: hidden;
      white-space: nowrap;
      user-select: none;
    }
    .marquee-content {
      display: inline-block;
      animation: marqueeScroll 25s linear infinite;
      white-space: nowrap;
    }
    .marquee-wrapper:hover .marquee-content,
    .marquee-wrapper:active .marquee-content {
      animation-play-state: paused;
    }

    @media (max-width: 768px) {
      .hide-mobile { display: none !important; }
      .container { padding: 0 16px; }
    }
    @media (min-width: 769px) {
      .hide-desktop { display: none !important; }
    }
  `}</style>
);

/* ─────────────────────────────────────────────────────────────────────────
   ROUTER – handles /papers/:filename redirection
   ───────────────────────────────────────────────────────────────────────── */
function useRouter() {
  const [route, setRoute] = useState(() => {
    const path = window.location.pathname;
    const segments = path.split("/").filter(Boolean);
    const raw = segments[0] || "home";
    const page = raw.toLowerCase();
    const id = segments[1] ? decodeURIComponent(segments[1]) : null;
    return { page, id };
  });
  const navigate = useCallback((page, id = null) => {
    const normalized = page.toLowerCase();
    const displaySlug = normalized === "apcwaiver" ? "APCwaiver" : (normalized === "home" ? "" : normalized);
    const path = id ? `/${displaySlug}/${encodeURIComponent(id)}` : `/${displaySlug}`;
    window.history.pushState({}, "", path);
    setRoute({ page: normalized, id });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  useEffect(() => {
    const onPop = () => {
      const path = window.location.pathname;
      const segments = path.split("/").filter(Boolean);
      const raw = segments[0] || "home";
      setRoute({ page: raw.toLowerCase(), id: segments[1] ? decodeURIComponent(segments[1]) : null });
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  return { route, navigate };
}

/* ─────────────────────────────────────────────────────────────────────────
   HELPERS FOR PDF REDIRECT
   ───────────────────────────────────────────────────────────────────────── */
function getSupabasePDFUrl(filename) {
  return `${SUPABASE_URL}/storage/v1/object/public/papers/${filename}`;
}

function extractFilenameFromUrl(url) {
  if (!url) return null;
  if (url.startsWith("/papers/")) {
    return url.replace("/papers/", "");
  }
  const match = url.match(/\/papers\/([^?]+)/);
  if (match) return match[1];
  if (url.endsWith(".pdf")) return url;
  return null;
}

/* ─────────────────────────────────────────────────────────────────────────
   NAVIGATION BAR (unchanged)
   ───────────────────────────────────────────────────────────────────────── */
function Nav({ page, navigate }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const links = [
    { key: "home", label: "Front Page" },
    { key: "archive", label: "Archive" },
    { key: "authors", label: "Author Index" },
    { key: "about", label: "About" },
    { key: "editorial", label: "Editorial Board" },
    { key: "guidelines", label: "Author Guidelines" },
    { key: "apc", label: "APC" },
    { key: "apcwaiver", label: "APC Waiver" },
    { key: "submit", label: "Submissions" },
  ];
  return (
    <nav
      style={{
        background: "white",
        borderBottom: scrolled ? "1px solid var(--ink-15)" : "1px solid transparent",
        boxShadow: scrolled ? "0 1px 20px rgba(0,0,0,0.08)" : "none",
        position: "sticky",
        top: 0,
        zIndex: 100,
        transition: "border-color 0.3s, box-shadow 0.3s",
      }}
    >
      <div className="container" style={{ display: "flex", alignItems: "center", height: "68px", gap: "32px" }}>
        <button
          onClick={() => navigate("home")}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            flexShrink: 0,
            display: "flex",
            alignItems: "baseline",
            gap: "1px",
          }}
        >
          <span style={{ fontFamily: "var(--serif-display)", fontSize: "22px", fontWeight: 900, color: "var(--ink)", letterSpacing: "-0.02em" }}>
            Science
          </span>
          <span style={{ fontFamily: "var(--serif-display)", fontSize: "22px", fontWeight: 900, color: "var(--scarlet)", fontStyle: "italic", letterSpacing: "-0.02em" }}>
            X
          </span>
          <span style={{ fontFamily: "var(--serif-display)", fontSize: "22px", fontWeight: 900, color: "var(--ink)", letterSpacing: "-0.02em" }}>
            plorer
          </span>
        </button>
        <div style={{ width: "1px", height: "28px", background: "var(--ink-15)" }} className="hide-mobile" />
        <div style={{ display: "flex", gap: "20px", alignItems: "center", flex: 1 }} className="hide-mobile">
          {links.map((l) => (
            <button key={l.key} onClick={() => navigate(l.key)} className={`nav-item ${page === l.key ? "active" : ""}`}>
              {l.label}
            </button>
          ))}
        </div>
        <button onClick={() => navigate("submit")} className="btn btn-primary btn-sm hide-mobile" style={{ marginLeft: "auto", flexShrink: 0 }}>
          Submit Paper
        </button>
        <button
          onClick={() => setMobileOpen((o) => !o)}
          className="hide-desktop"
          style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", padding: "8px", display: "flex", flexDirection: "column", gap: "5px" }}
        >
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              style={{
                display: "block", width: "22px", height: "1.5px", background: "var(--ink)", transition: "0.2s",
                transform: mobileOpen ? i === 0 ? "rotate(45deg) translate(4.5px,4.5px)" : i === 2 ? "rotate(-45deg) translate(4.5px,-4.5px)" : "scaleX(0)" : "none",
              }}
            />
          ))}
        </button>
      </div>
      {mobileOpen && (
        <div style={{ background: "white", borderTop: "1px solid var(--ink-15)", padding: "16px 24px 20px" }} className="hide-desktop animate-fade">
          {links.map((l) => (
            <button
              key={l.key}
              onClick={() => { navigate(l.key); setMobileOpen(false); }}
              style={{
                display: "block", width: "100%", textAlign: "left", padding: "12px 0",
                fontFamily: "var(--mono)", fontSize: "11px", letterSpacing: "0.1em", textTransform: "uppercase",
                color: page === l.key ? "var(--scarlet)" : "var(--ink)",
                background: "none", border: "none", cursor: "pointer", borderBottom: "1px solid var(--ink-07)",
              }}
            >
              {l.label}
            </button>
          ))}
        </div>
      )}
    </nav>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   ARTICLE CARD (unchanged)
   ───────────────────────────────────────────────────────────────────────── */
function ArticleCard({ article, navigate, variant = "default", delay = 0 }) {
  const catColor = CATEGORY_COLORS[article.category] || "#666";
  const pubDate = formatDate(article.published_at);
  if (variant === "featured") {
    return (
      <div className="animate-up" style={{ animationDelay: `${delay}s`, borderBottom: "1px solid var(--ink-15)", paddingBottom: "40px", marginBottom: "40px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "14px" }}>
          <span className="cat-pill" style={{ background: `${catColor}18`, color: catColor }}>{article.category}</span>
          {pubDate && (
            <span style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "var(--ink-40)", letterSpacing: "0.05em" }}>
              {pubDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
            </span>
          )}
        </div>
        <button onClick={() => navigate("article", article.id)} style={{ background: "none", border: "none", textAlign: "left", cursor: "pointer", display: "block", width: "100%", padding: 0 }}>
          <h2
            className="display-md"
            style={{ color: "var(--ink)", marginBottom: "12px", transition: "color 0.2s" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--scarlet)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ink)")}
          >
            {article.title}
          </h2>
        </button>
        <div style={{ fontFamily: "var(--serif-body)", fontStyle: "italic", color: "var(--ink-70)", fontSize: "15px", marginBottom: "14px", display: "flex", flexWrap: "wrap", gap: "6px 12px" }}>
          {parseAuthors(article.authors).map((author) => (
            <button
              key={author}
              onClick={(e) => {
                e.stopPropagation();
                navigate("authors", encodeURIComponent(author));
              }}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                fontFamily: "inherit",
                fontStyle: "italic",
                fontSize: "15px",
                color: "var(--ink-70)",
                textDecoration: "underline",
                textDecorationColor: "transparent",
                transition: "color 0.15s, text-decoration-color 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "var(--scarlet)";
                e.currentTarget.style.textDecorationColor = "var(--scarlet)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "var(--ink-70)";
                e.currentTarget.style.textDecorationColor = "transparent";
              }}
              title={`View ${author} in Author Index`}
            >
              {author}
            </button>
          ))}
        </div>
        {article.abstract && (
          <p style={{ color: "var(--ink-70)", fontSize: "15px", lineHeight: "1.7", marginBottom: "16px", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {article.abstract}
          </p>
        )}
        <button
          onClick={() => navigate("article", article.id)}
          style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "var(--mono)", fontSize: "10px", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--scarlet)", padding: 0, display: "flex", alignItems: "center", gap: "6px" }}
        >
          Read Article <span style={{ transition: "transform 0.2s" }}>→</span>
        </button>
      </div>
    );
  }
  return (
    <div className="animate-up" style={{ animationDelay: `${delay}s`, borderBottom: "1px solid var(--ink-07)", paddingBottom: "20px", marginBottom: "20px" }}>
      <span className="cat-pill" style={{ background: `${catColor}15`, color: catColor, marginBottom: "8px", display: "inline-block" }}>{article.category}</span>
      <button onClick={() => navigate("article", article.id)} style={{ background: "none", border: "none", textAlign: "left", cursor: "pointer", display: "block", width: "100%", padding: 0 }}>
        <h4
          style={{ fontFamily: "var(--serif-display)", fontSize: "16px", fontWeight: 600, color: "var(--ink)", lineHeight: "1.35", transition: "color 0.2s" }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "var(--scarlet)")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ink)")}
        >
          {article.title}
        </h4>
      </button>
      <div style={{ fontFamily: "var(--serif-body)", fontStyle: "italic", color: "var(--ink-40)", fontSize: "13px", marginTop: "5px", display: "flex", flexWrap: "wrap", gap: "4px 8px" }}>
        {parseAuthors(article.authors).map((author) => (
          <button
            key={author}
            onClick={(e) => {
              e.stopPropagation();
              navigate("authors", encodeURIComponent(author));
            }}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              fontFamily: "inherit",
              fontStyle: "italic",
              fontSize: "13px",
              color: "var(--ink-40)",
              transition: "color 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--scarlet)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ink-40)")}
            title={`View ${author} in Author Index`}
          >
            {author}
          </button>
        ))}
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div style={{ borderBottom: "1px solid var(--ink-15)", paddingBottom: "40px", marginBottom: "40px" }}>
      <div className="skeleton" style={{ width: "100px", height: "20px", marginBottom: "14px" }} />
      <div className="skeleton" style={{ width: "85%", height: "28px", marginBottom: "10px" }} />
      <div className="skeleton" style={{ width: "60%", height: "28px", marginBottom: "16px" }} />
      <div className="skeleton" style={{ width: "200px", height: "16px", marginBottom: "12px" }} />
      <div className="skeleton" style={{ width: "100%", height: "14px", marginBottom: "6px" }} />
      <div className="skeleton" style={{ width: "92%", height: "14px", marginBottom: "6px" }} />
      <div className="skeleton" style={{ width: "78%", height: "14px" }} />
    </div>
  );
}

function MarqueeCategories({ categories, activeCategory, setActiveCategory }) {
  const doubled = [...categories, ...categories];
  return (
    <div className="marquee-wrapper" style={{ width: "100%", background: "white", borderBottom: "1px solid var(--ink-15)" }}>
      <div className="marquee-content">
        {doubled.map((cat, idx) => (
          <button
            key={`${cat}-${idx}`}
            onClick={() => setActiveCategory(cat === "All Fields" ? null : cat)}
            style={{
              display: "inline-block", padding: "14px 18px", background: "none", border: "none", cursor: "pointer",
              fontFamily: "var(--mono)", fontSize: "10px", letterSpacing: "0.08em", textTransform: "uppercase",
              color: activeCategory === (cat === "All Fields" ? null : cat) ? "var(--scarlet)" : "var(--ink-70)",
              borderBottom: `2px solid ${activeCategory === (cat === "All Fields" ? null : cat) ? "var(--scarlet)" : "transparent"}`,
              transition: "color 0.2s, border-color 0.2s", whiteSpace: "nowrap",
            }}
          >
            {cat}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   HOME PAGE (unchanged)
   ───────────────────────────────────────────────────────────────────────── */
function HomePage({ navigate }) {
  const [activeCategory, setActiveCategory] = useState(null);
  const cacheKey = `articles:${activeCategory || "all"}:10`;
  const [articles, setArticles] = useState(() => getCached("articles:all:10") || []);
  const [loading, setLoading] = useState(() => !getCached("articles:all:10"));

  useEffect(() => {
    const cached = getCached(cacheKey);
    if (cached) {
      setArticles(cached);
      setLoading(false);
    } else {
      setLoading(true);
    }
    let isMounted = true;
    fetchArticles({ limit: 10, category: activeCategory }).then((rows) => {
      if (isMounted && rows) {
        setArticles(rows);
        setLoading(false);
      }
    });
    return () => { isMounted = false; };
  }, [activeCategory, cacheKey]);

  const featured = articles.slice(0, 4);
  const sidebar = articles.slice(4, 9);
  const categoriesWithAll = ["All Fields", ...CATEGORY_LIST];
  return (
    <div>
      <div style={{ background: "var(--ink)", color: "white", padding: "80px 0 72px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle at 70% 50%, rgba(192,57,43,0.15) 0%, transparent 60%)", pointerEvents: "none" }} />
        <div className="container" style={{ position: "relative" }}>
          <div style={{ maxWidth: "700px" }}>
            <div className="label animate-up" style={{ color: "rgba(255,255,255,0.5)", marginBottom: "20px" }}>Premier Open-Access Research Journal</div>
            <h1 className="display-xl animate-up animate-up-d1" style={{ color: "white", marginBottom: "24px" }}>
              Where discovery<br /><span style={{ color: "var(--scarlet)", fontStyle: "italic" }}>meets rigor.</span>
            </h1>
            <p className="animate-up animate-up-d2" style={{ fontSize: "18px", color: "rgba(255,255,255,0.65)", lineHeight: "1.7", maxWidth: "540px", marginBottom: "36px" }}>
              High-impact, peer-reviewed research across all academic disciplines. Open to the world, uncompromising in quality.
            </p>
            <div className="animate-up animate-up-d3" style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              <button onClick={() => navigate("submit")} className="btn btn-primary">Submit Manuscript</button>
              <button onClick={() => navigate("archive")} className="btn" style={{ borderColor: "rgba(255,255,255,0.3)", color: "white" }}>Browse Archive</button>
            </div>
          </div>
          <div className="animate-up animate-up-d4" style={{ display: "flex", gap: "0", marginTop: "60px", borderTop: "1px solid rgba(255,255,255,0.1)", paddingTop: "32px", flexWrap: "wrap" }}>
            {[
              { label: "Access Model", value: "Open Access" },
              { label: "Peer Review", value: "Double-Blind" },
              { label: "Copyright", value: "CC-BY 4.0" },
              { label: "Publication Fee", value: "Case-by-case APC" },
            ].map((s, i) => (
              <div key={i} style={{ flex: "1", minWidth: "120px", paddingRight: "32px", marginRight: "32px", borderRight: i < 3 ? "1px solid rgba(255,255,255,0.1)" : "none" }}>
                <div style={{ fontFamily: "var(--serif-display)", fontSize: "24px", fontWeight: 700, color: "white", lineHeight: 1.2 }}>{s.value}</div>
                <div style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "rgba(255,255,255,0.4)", letterSpacing: "0.08em", textTransform: "uppercase", marginTop: "6px" }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div style={{ background: "white", borderBottom: "1px solid var(--ink-15)", position: "sticky", top: "68px", zIndex: 50 }}>
        <div className="container">
          <div className="hide-mobile" style={{ display: "flex", gap: "0", overflowX: "auto", WebkitOverflowScrolling: "touch", scrollbarWidth: "none" }}>
            {categoriesWithAll.map((cat) => (
              <button
                key={cat || "all"}
                onClick={() => setActiveCategory(cat === "All Fields" ? null : cat)}
                style={{
                  flexShrink: 0, padding: "14px 18px", background: "none", border: "none", cursor: "pointer",
                  fontFamily: "var(--mono)", fontSize: "10px", letterSpacing: "0.08em", textTransform: "uppercase",
                  color: activeCategory === (cat === "All Fields" ? null : cat) ? "var(--scarlet)" : "var(--ink-70)",
                  borderBottom: `2px solid ${activeCategory === (cat === "All Fields" ? null : cat) ? "var(--scarlet)" : "transparent"}`,
                  transition: "color 0.2s, border-color 0.2s", whiteSpace: "nowrap",
                }}
              >
                {cat}
              </button>
            ))}
          </div>
          <div className="hide-desktop">
            <MarqueeCategories categories={categoriesWithAll} activeCategory={activeCategory} setActiveCategory={setActiveCategory} />
          </div>
        </div>
      </div>
      <div className="container" style={{ padding: "56px 24px" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "36px" }}>
            <div className="rule-heavy" style={{ flex: 1 }} />
            <span className="label" style={{ color: "var(--ink-40)", whiteSpace: "nowrap" }}>{activeCategory || "Latest Research"}</span>
          </div>
          {loading ? (
            [1, 2, 3].map((i) => <SkeletonCard key={i} />)
          ) : featured.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 0" }}>
              <div style={{ fontFamily: "var(--serif-display)", fontSize: "48px", opacity: 0.2 }}>∅</div>
              <p style={{ color: "var(--ink-40)", fontStyle: "italic" }}>No articles in this category yet.</p>
            </div>
          ) : (
            featured.map((a, i) => <ArticleCard key={a.id} article={a} navigate={navigate} variant="featured" delay={i * 0.06} />)
          )}
        </div>
        <div style={{ marginTop: "64px" }}>
          <div className="rule-heavy" style={{ marginBottom: "32px" }} />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "32px" }}>
            <div style={{ background: "white", border: "1px solid var(--ink-15)", padding: "24px" }}>
              <div className="label" style={{ color: "var(--ink-40)", marginBottom: "16px" }}>Journal Information</div>
              <div className="rule-heavy" style={{ marginBottom: "20px" }} />
              {[
                { k: "Review Process", v: "Double-Blind Peer Review" },
                { k: "Access Policy", v: "Open Access (CC-BY 4.0)" },
                { k: "Publication Fee", v: "Case-by-case (Waivers Available)" },
                { k: "Format", v: "Electronic (PDF / Full-Text)" },
                { k: "Publisher", v: "ScienceXplorer Press" },
              ].map(({ k, v }) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "9px 0", borderBottom: "1px solid var(--ink-07)" }}>
                  <span style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "var(--ink-40)", letterSpacing: "0.05em", textTransform: "uppercase" }}>{k}</span>
                  <span style={{ fontFamily: "var(--serif-display)", fontSize: "14px", fontWeight: 600 }}>{v}</span>
                </div>
              ))}
            </div>
            {!loading && sidebar.length > 0 && (
              <div>
                <div className="label" style={{ color: "var(--ink-40)", marginBottom: "14px" }}>Also In This Issue</div>
                <div className="rule-heavy" style={{ marginBottom: "20px" }} />
                {sidebar.map((a, i) => <ArticleCard key={a.id} article={a} navigate={navigate} variant="compact" delay={i * 0.05} />)}
              </div>
            )}
            <div style={{ background: "var(--scarlet)", padding: "28px" }}>
              <div style={{ fontFamily: "var(--serif-display)", fontSize: "20px", fontWeight: 700, color: "white", marginBottom: "10px", lineHeight: 1.3 }}>
                Ready to publish your research?
              </div>
              <p style={{ fontSize: "13px", color: "rgba(255,255,255,0.75)", marginBottom: "20px", lineHeight: 1.6 }}>
                Publish your research with ScienceXplorer and make your work accessible worldwide.
              </p>
              <button onClick={() => navigate("submit")} className="btn" style={{ color: "white", borderColor: "rgba(255,255,255,0.5)", width: "100%" }}>
                Submit Manuscript
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   CITE BLOCK (unchanged)
   ───────────────────────────────────────────────────────────────────────── */
function CiteBlock({ fmt, text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <div style={{ marginBottom: "16px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
        <span style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "var(--scarlet)", letterSpacing: "0.1em", textTransform: "uppercase" }}>{fmt}</span>
        <button
          onClick={copy}
          style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "var(--mono)", fontSize: "10px", color: copied ? "#16A34A" : "var(--ink-40)", letterSpacing: "0.08em", textTransform: "uppercase", display: "flex", alignItems: "center", gap: "5px" }}
        >
          {copied ? (
            <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20,6 9,17 4,12" /></svg>Copied</>
          ) : (
            <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" /></svg>Copy</>
          )}
        </button>
      </div>
      <pre style={{ fontFamily: "var(--mono)", fontSize: "12px", lineHeight: "1.7", color: "var(--ink-70)", background: "var(--ink-07)", padding: "14px 16px", whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0 }}>
        {text}
      </pre>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   ARTICLE PAGE – PDF link uses relative URL
   ───────────────────────────────────────────────────────────────────────── */
function ArticlePage({ id, navigate }) {
  const [article, setArticle] = useState(() => getCached(`article:${id}`));
  const [loading, setLoading] = useState(() => !getCached(`article:${id}`));
  const [citeOpen, setCiteOpen] = useState(false);
  const [inspectOpen, setInspectOpen] = useState(false);
  const [pdfCopied, setPdfCopied] = useState(false);

  useEffect(() => {
    const cached = getCached(`article:${id}`);
    if (cached) {
      setArticle(cached);
      setLoading(false);
    } else {
      setLoading(true);
    }
    let isMounted = true;
    fetchArticle(id).then((a) => {
      if (isMounted) {
        setArticle(a);
        setLoading(false);
      }
    });
    return () => { isMounted = false; };
  }, [id]);

  useEffect(() => {
    if (!article) return;
    const enriched = enrichManuscriptWithDoi(article);
    const tags = generateHighwireTags(enriched);
    const inserted = tags.map(({ name, content }) => {
      if (!content) return null;
      const el = document.createElement("meta");
      el.setAttribute("name", name);
      el.setAttribute("content", content);
      el.setAttribute("data-scholar", "true");
      document.head.appendChild(el);
      return el;
    }).filter(Boolean);

    // ScholarlyArticle JSON-LD
    const jsonLd = generateScholarlyJsonLd(enriched);
    let scriptEl = null;
    if (jsonLd) {
      scriptEl = document.createElement("script");
      scriptEl.type = "application/ld+json";
      scriptEl.setAttribute("data-scholar", "true");
      scriptEl.text = JSON.stringify(jsonLd);
      document.head.appendChild(scriptEl);
    }

    const prevTitle = document.title;
    document.title = `${article.title} — ScienceXplorer`;
    return () => {
      inserted.forEach((el) => el.parentNode && el.parentNode.removeChild(el));
      if (scriptEl && scriptEl.parentNode) scriptEl.parentNode.removeChild(scriptEl);
      document.title = prevTitle;
    };
  }, [article]);

  const copyPdfLink = () => {
    if (article?.pdf_url) {
      const link = window.location.origin + article.pdf_url;
      navigator.clipboard.writeText(link);
      setPdfCopied(true);
      setTimeout(() => setPdfCopied(false), 2000);
    }
  };

  const parsedAuthors = useMemo(() => {
    return article ? parseAuthors(article.authors) : [];
  }, [article]);

  if (loading)
    return (
      <div className="container" style={{ padding: "80px 24px", maxWidth: "800px", margin: "0 auto" }}>
        {[80, 60, 40, 200, 180, 160].map((w, i) => (
          <div key={i} className="skeleton" style={{ width: `${w}%`, maxWidth: "100%", height: i < 3 ? "24px" : "14px", marginBottom: "16px" }} />
        ))}
      </div>
    );
  if (!article)
    return (
      <div className="container" style={{ padding: "120px 24px", textAlign: "center" }}>
        <div style={{ fontFamily: "var(--serif-display)", fontSize: "64px", opacity: 0.15 }}>404</div>
        <p style={{ color: "var(--ink-40)", fontStyle: "italic", marginBottom: "24px" }}>Article not found or unpublished.</p>
        <button onClick={() => navigate("home")} className="btn">Return Home</button>
      </div>
    );
  const catColor = CATEGORY_COLORS[article.category] || "#666";
  const pubDate = formatDate(article.published_at);
  const manuscriptDoi = getManuscriptDoi(article);

  return (
    <div style={{ padding: "56px 0 80px" }}>
      <div className="container">
        <div style={{ maxWidth: "760px", margin: "0 auto" }}>
          <div className="animate-up" style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "28px" }}>
            <button onClick={() => navigate("home")} style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "var(--ink-40)", background: "none", border: "none", cursor: "pointer", letterSpacing: "0.08em", textTransform: "uppercase", padding: 0 }}>
              Front Page
            </button>
            <span style={{ color: "var(--ink-15)" }}>›</span>
            <span className="cat-pill" style={{ background: `${catColor}18`, color: catColor }}>{article.category}</span>
          </div>
          <h1 className="display-lg animate-up animate-up-d1" style={{ marginBottom: "20px", lineHeight: "1.2" }}>{article.title}</h1>

          {/* Authors Row with Scholar Indexing Mechanisms */}
          <div className="animate-up animate-up-d2" style={{ paddingBottom: "24px", borderBottom: "1px solid var(--ink-15)", marginBottom: "36px" }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center", marginBottom: "12px" }}>
              {parsedAuthors.map((authorName) => (
                <div
                  key={authorName}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    background: "white",
                    border: "1px solid var(--ink-15)",
                    padding: "4px 10px",
                    borderRadius: "2px",
                  }}
                >
                  <button
                    onClick={() => navigate("authors", encodeURIComponent(authorName))}
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      fontFamily: "var(--serif-body)",
                      fontSize: "15px",
                      color: "var(--ink)",
                      fontWeight: 600,
                    }}
                    title={`View ${authorName} in Author Index`}
                  >
                    {authorName}
                  </button>
                  <a
                    href={getGoogleScholarAuthorUrl(authorName)}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      fontFamily: "var(--mono)",
                      fontSize: "9px",
                      background: "var(--ink-07)",
                      color: "var(--ink-70)",
                      padding: "2px 6px",
                      borderRadius: "2px",
                      textDecoration: "none",
                      letterSpacing: "0.04em",
                    }}
                    title={`Search ${authorName} on Google Scholar`}
                  >
                    🎓 Scholar ↗
                  </a>
                </div>
              ))}
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", alignItems: "center" }}>
              {article.affiliations && <span style={{ fontFamily: "var(--mono)", fontSize: "11px", color: "var(--ink-70)", letterSpacing: "0.03em" }}>{article.affiliations}</span>}
              {pubDate && <span style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "var(--ink-40)", letterSpacing: "0.05em" }}>{pubDate.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</span>}
              {article.views > 0 && <span style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "var(--ink-40)", letterSpacing: "0.05em" }}>{article.views.toLocaleString()} views</span>}
            </div>
          </div>

          <div className="animate-up animate-up-d3">
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "18px" }}>
              <span className="label" style={{ color: "var(--ink-40)" }}>Abstract</span>
              <div className="rule" style={{ flex: 1 }} />
            </div>
            <p style={{ fontSize: "17px", lineHeight: "1.85", color: "var(--ink)", fontWeight: 300, marginBottom: "40px" }}>{article.abstract}</p>
          </div>

          <div className="animate-up animate-up-d4" style={{ background: "var(--ink-07)", padding: "20px 24px", marginBottom: "24px", display: "flex", flexWrap: "wrap", gap: "20px", alignItems: "center" }}>
            <div><div className="label" style={{ color: "var(--ink-40)", marginBottom: "4px" }}>Journal</div><div style={{ fontFamily: "var(--serif-display)", fontSize: "14px", fontWeight: 600 }}>ScienceXplorer</div></div>
            <div><div className="label" style={{ color: "var(--ink-40)", marginBottom: "4px" }}>Field</div><div style={{ fontFamily: "var(--serif-display)", fontSize: "14px", fontWeight: 600 }}>{article.category}</div></div>
            <div><div className="label" style={{ color: "var(--ink-40)", marginBottom: "4px" }}>License</div><div style={{ fontFamily: "var(--serif-display)", fontSize: "14px", fontWeight: 600 }}>CC-BY 4.0</div></div>
            {(article.volume || article.issue) && (
              <div><div className="label" style={{ color: "var(--ink-40)", marginBottom: "4px" }}>Citation</div><div style={{ fontFamily: "var(--mono)", fontSize: "12px", fontWeight: 600 }}>Vol. {article.volume || "—"}, No. {article.issue || "—"}</div></div>
            )}
            {manuscriptDoi && (
              <div>
                <div className="label" style={{ color: "var(--ink-40)", marginBottom: "4px" }}>DOI</div>
                <a
                  href={manuscriptDoi}
                  target="_blank"
                  rel="noreferrer"
                  style={{ fontFamily: "var(--mono)", fontSize: "12px", fontWeight: 600, color: "var(--scarlet)", textDecoration: "underline", display: "inline-flex", alignItems: "center", gap: "4px", wordBreak: "break-all" }}
                >
                  {manuscriptDoi} ↗
                </a>
              </div>
            )}
          </div>

          {/* Google Scholar Indexing Mechanism Bar */}
          <div
            className="animate-up animate-up-d4"
            style={{
              background: "#F8FAFC",
              border: "1px solid #E2E8F0",
              padding: "16px 20px",
              marginBottom: "32px",
              display: "flex",
              flexWrap: "wrap",
              gap: "14px",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ fontSize: "18px" }}>🎓</span>
              <div>
                <div style={{ fontFamily: "var(--mono)", fontSize: "10px", fontWeight: 700, color: "#166534", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                  Google Scholar & Academic Indexing: Active
                </div>
                <div style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "#64748B", marginTop: "2px" }}>
                  Highwire Press tags embedded · Open-access crawler permitted
                </div>
              </div>
            </div>

            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
              <a
                href={getGoogleScholarArticleUrl(article.title, manuscriptDoi)}
                target="_blank"
                rel="noreferrer"
                className="btn btn-sm"
                style={{
                  fontFamily: "var(--mono)",
                  fontSize: "10px",
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  background: "white",
                  borderColor: "#CBD5E1",
                  color: "#1E293B",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  padding: "6px 12px",
                }}
              >
                Search on Scholar ↗
              </a>
              <button
                onClick={() => setInspectOpen(true)}
                className="btn btn-ghost btn-sm"
                style={{
                  fontFamily: "var(--mono)",
                  fontSize: "10px",
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  padding: "6px 12px",
                }}
              >
                Inspect Scholar Tags
              </button>
              <button
                onClick={() => navigate("authors")}
                className="btn btn-ghost btn-sm"
                style={{
                  fontFamily: "var(--mono)",
                  fontSize: "10px",
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  padding: "6px 12px",
                }}
              >
                Author Index →
              </button>
            </div>
          </div>

          <div className="animate-up" style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            {article.pdf_url && (
              <>
                <a href={article.pdf_url} className="btn btn-primary">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14,2 14,8 20,8" /></svg>
                  Download PDF
                </a>
                <button onClick={copyPdfLink} className="btn btn-ghost">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" /></svg>
                  {pdfCopied ? "Copied!" : "Copy PDF link"}
                </button>
              </>
            )}
            <button onClick={() => setCiteOpen(true)} className="btn">How to Cite</button>
            <button onClick={() => navigate("home")} className="btn btn-ghost">← Back to Journal</button>
          </div>

          {/* Inspect Live Scholar Meta Tags Modal */}
          {inspectOpen && (
            <div onClick={() => setInspectOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 220, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
              <div onClick={(e) => e.stopPropagation()} className="animate-up" style={{ background: "white", width: "100%", maxWidth: "680px", maxHeight: "85vh", overflowY: "auto", padding: "32px", position: "relative" }}>
                <button onClick={() => setInspectOpen(false)} style={{ position: "absolute", top: "16px", right: "20px", background: "none", border: "none", cursor: "pointer", fontSize: "22px", color: "var(--ink-40)" }}>×</button>
                <div className="label" style={{ color: "var(--scarlet)", marginBottom: "8px" }}>Live Highwire Press & Dublin Core Tags</div>
                <h3 style={{ fontFamily: "var(--serif-display)", fontSize: "18px", fontWeight: 700, marginBottom: "16px", lineHeight: 1.3 }}>{article.title}</h3>
                <p style={{ fontSize: "13px", color: "var(--ink-70)", marginBottom: "14px" }}>These tags are actively injected in the HTML &lt;head&gt; of this page for Google Scholar and Googlebot crawlers:</p>
                <div style={{ background: "#0F172A", color: "#E2E8F0", padding: "16px", fontFamily: "var(--mono)", fontSize: "11px", lineHeight: 1.7, overflowX: "auto", marginBottom: "20px" }}>
                  {generateHighwireTags(enrichManuscriptWithDoi(article)).map((t, idx) => (
                    <div key={idx}>&lt;meta name="{t.name}" content="{t.content}" /&gt;</div>
                  ))}
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                  <button
                    onClick={() => {
                      const str = generateHighwireTags(enrichManuscriptWithDoi(article)).map((t) => `<meta name="${t.name}" content="${t.content}" />`).join("\n");
                      navigator.clipboard.writeText(str);
                    }}
                    className="btn btn-primary btn-sm"
                  >
                    Copy Meta Tags
                  </button>
                  <button onClick={() => setInspectOpen(false)} className="btn btn-ghost btn-sm">Close</button>
                </div>
              </div>
            </div>
          )}
          {citeOpen && (
            <div onClick={() => setCiteOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
              <div onClick={(e) => e.stopPropagation()} className="animate-up" style={{ background: "white", width: "100%", maxWidth: "620px", padding: "36px", position: "relative" }}>
                <button onClick={() => setCiteOpen(false)} style={{ position: "absolute", top: "16px", right: "20px", background: "none", border: "none", cursor: "pointer", fontSize: "22px", color: "var(--ink-40)", lineHeight: 1 }}>×</button>
                <div className="label" style={{ color: "var(--ink-40)", marginBottom: "10px" }}>Cite This Article</div>
                <h3 style={{ fontFamily: "var(--serif-display)", fontSize: "18px", fontWeight: 700, marginBottom: "24px", paddingRight: "24px", lineHeight: 1.3 }}>{article.title}</h3>
                {(() => {
                  const mDoi = getManuscriptDoi(article);
                  const doiPart = mDoi ? ` ${mDoi}` : "";
                  const mlaDoi = mDoi ? `, ${mDoi}` : "";
                  const bibtexDoi = mDoi ? `,\n  doi     = {${getDoiDisplay(mDoi)}}` : "";
                  return [
                    { fmt: "APA 7th", text: `${article.authors} (${pubDate ? pubDate.getFullYear() : "n.d."}). ${article.title}. ScienceXplorer${article.volume ? `, ${article.volume}` : ""}${article.issue ? `(${article.issue})` : ""}.${doiPart}` },
                    { fmt: "MLA 9th", text: `${article.authors}. "${article.title}." ScienceXplorer${article.volume ? `, vol. ${article.volume}` : ""}${article.issue ? `, no. ${article.issue}` : ""}, ${pubDate ? pubDate.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" }) : "n.d."}${mlaDoi}.` },
                    { fmt: "Chicago 17th", text: `${article.authors}. "${article.title}." ScienceXplorer${article.volume ? ` ${article.volume}` : ""}${article.issue ? `, no. ${article.issue}` : ""} (${pubDate ? pubDate.getFullYear() : "n.d."})${mDoi ? `. ${mDoi}` : ""}.` },
                    { fmt: "BibTeX", text: `@article{sx${article.id},\n  author  = {${article.authors || "Author, A."}},\n  title   = {${article.title}},\n  journal = {ScienceXplorer},\n  year    = {${pubDate ? pubDate.getFullYear() : "YYYY"}},\n  volume  = {${article.volume || ""}},\n  number  = {${article.issue || ""}},\n  url     = {${window.location.href}}${bibtexDoi}\n}` },
                  ].map(({ fmt, text }) => <CiteBlock key={fmt} fmt={fmt} text={text} />);
                })()}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   DROPZONE (unchanged)
   ───────────────────────────────────────────────────────────────────────── */
function Dropzone({ file, onFile, error }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useCallback((node) => { if (node) node.value = ""; }, []);
  const accept = (f) => {
    if (!f) return;
    const ok = ["application/pdf", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
    const extOk = f.name.match(/\.(pdf|docx)$/i);
    if (!ok.includes(f.type) && !extOk) return window.alert("Only PDF or DOCX files are accepted.");
    if (f.size > 25 * 1024 * 1024) return window.alert("File exceeds 25 MB limit.");
    onFile(f);
  };
  const fmt = (bytes) => bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
  return (
    <div
      onClick={() => document.getElementById("dz-input").click()}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => { e.preventDefault(); setDragging(false); accept(e.dataTransfer.files[0]); }}
      style={{ border: `2px dashed ${error ? "var(--scarlet)" : dragging ? "var(--ink)" : "var(--ink-15)"}`, background: dragging ? "var(--ink-07)" : file ? "#F0FDF4" : "white", padding: "28px 20px", textAlign: "center", cursor: "pointer", transition: "border-color 0.2s, background 0.2s" }}
    >
      <input id="dz-input" type="file" accept=".pdf,.docx" style={{ display: "none" }} ref={inputRef} onChange={(e) => accept(e.target.files[0])} />
      {file ? (
        <div>
          <div style={{ fontSize: "28px", marginBottom: "8px" }}>📄</div>
          <div style={{ fontFamily: "var(--serif-display)", fontSize: "15px", fontWeight: 600, color: "#166534", marginBottom: "4px" }}>{file.name}</div>
          <div style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "#4ADE80", letterSpacing: "0.05em" }}>{fmt(file.size)} · Click to change</div>
        </div>
      ) : (
        <div>
          <div style={{ fontSize: "28px", marginBottom: "10px" }}>📁</div>
          <div style={{ fontFamily: "var(--serif-display)", fontSize: "15px", fontWeight: 600, color: "var(--ink)", marginBottom: "4px" }}>{dragging ? "Drop it here" : "Click to upload or drag & drop"}</div>
          <div style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "var(--ink-40)", letterSpacing: "0.05em" }}>PDF or DOCX · max 25 MB</div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   SUBMIT PAGE
   ───────────────────────────────────────────────────────────────────────── */
function SubmitPage({ navigate }) {
  const EMPTY = { title: "", authors: "", affiliations: "", author_email: "", category: "", abstract: "", keywords: "", cover_letter: "" };
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const validate = (f) => {
    const e = {};
    if (!f.title.trim()) e.title = "Title is required";
    if (!f.authors.trim()) e.authors = "Author name(s) required";
    if (!f.affiliations.trim()) e.affiliations = "Author affiliation(s) required";
    if (!f.author_email.trim()) e.author_email = "Email is required";
    else if (!/^[^@]+@[^@]+\.[^@]+$/.test(f.author_email)) e.author_email = "Valid email required";
    if (!f.category) e.category = "Please select a category";
    if (!f.abstract.trim()) e.abstract = "Abstract is required";
    else if (f.abstract.trim().length < 100) e.abstract = "Abstract should be at least 100 characters";
    return e;
  };
  const set = (field, value) => {
    const next = { ...form, [field]: value };
    setForm(next);
    if (touched[field]) setErrors(validate(next));
  };
  const blur = (field) => {
    setTouched((t) => ({ ...t, [field]: true }));
    setErrors(validate(form));
  };
  const send = async () => {
    const allTouched = Object.fromEntries(Object.keys(form).map((k) => [k, true]));
    setTouched(allTouched);
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setLoading(true);
    try {
      let pdf_url = null;
      if (file) {
        pdf_url = await uploadPDF(file, form.title);
      }
      await insertSubmission({ ...form, pdf_url, submitted_at: new Date().toISOString() });
      setDone(true);
    } catch (e) {
      window.alert(e.message);
    } finally {
      setLoading(false);
    }
  };
  if (done)
    return (
      <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div className="container" style={{ textAlign: "center", maxWidth: "500px", padding: "80px 24px" }}>
          <div className="animate-up" style={{ width: "64px", height: "64px", background: "#DCFCE7", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px" }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2.5"><polyline points="20,6 9,17 4,12" /></svg>
          </div>
          <h2 className="display-md animate-up animate-up-d1" style={{ marginBottom: "14px" }}>Submission Received</h2>
          <p className="animate-up animate-up-d2" style={{ color: "var(--ink-70)", lineHeight: 1.7, marginBottom: "28px" }}>
            Your manuscript has been submitted to our editorial board. You'll receive a confirmation at <strong>{form.author_email}</strong> within 24 hours.
          </p>
          <button onClick={() => { setDone(false); setForm(EMPTY); setFile(null); }} className="btn btn-ghost animate-up animate-up-d3">
            Submit Another
          </button>
        </div>
      </div>
    );
  const FW = ({ name, label, required, hint, children }) => (
    <div>
      <label className="field-label" htmlFor={name}>
        {label} {required && <span style={{ color: "var(--scarlet)" }}>*</span>}
        {hint && <span style={{ fontWeight: 400, color: "var(--ink-40)", textTransform: "none", letterSpacing: 0, marginLeft: "6px", fontSize: "11px" }}>{hint}</span>}
      </label>
      {children}
      {errors[name] && touched[name] && (
        <div style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "var(--scarlet)", marginTop: "5px", letterSpacing: "0.05em" }}>↑ {errors[name]}</div>
      )}
    </div>
  );
  const hasErr = (n) => (errors[n] && touched[n] ? { borderColor: "var(--scarlet)" } : {});
  return (
    <div style={{ padding: "60px 0 100px" }}>
      <div className="container" style={{ maxWidth: "720px", margin: "0 auto" }}>
        <div className="animate-up" style={{ marginBottom: "48px" }}>
          <div className="label" style={{ color: "var(--ink-40)", marginBottom: "12px" }}>Editorial Portal</div>
          <h1 className="display-lg" style={{ marginBottom: "14px" }}>Submit Manuscript</h1>
          <p style={{ color: "var(--ink-70)", fontSize: "16px", lineHeight: 1.7 }}>All submissions undergo rigorous double-blind peer review. Initial editorial desk assessment is completed promptly upon submission.</p>
        </div>
        <div className="rule-heavy animate-up" style={{ marginBottom: "40px" }} />
        <div className="animate-up animate-up-d1" style={{ display: "flex", flexDirection: "column", gap: "28px" }}>
          <FW name="title" label="Manuscript Title" required>
            <input id="title" className="field" placeholder="Enter the full title" value={form.title} onChange={(e) => set("title", e.target.value)} onBlur={() => blur("title")} style={hasErr("title")} />
          </FW>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
            <FW name="authors" label="All Authors" required hint="separate by semicolon">
              <input id="authors" className="field" placeholder="Last, First; Last, First" value={form.authors} onChange={(e) => set("authors", e.target.value)} onBlur={() => blur("authors")} style={hasErr("authors")} />
            </FW>
            <FW name="affiliations" label="Author Affiliations" required hint="semicolon‑separated, same order as authors">
              <input id="affiliations" className="field" placeholder="University of Example; Another Institution" value={form.affiliations} onChange={(e) => set("affiliations", e.target.value)} onBlur={() => blur("affiliations")} style={hasErr("affiliations")} />
            </FW>
          </div>
          <FW name="author_email" label="Corresponding Email" required>
            <input id="author_email" className="field" type="email" placeholder="author@institution.edu" value={form.author_email} onChange={(e) => set("author_email", e.target.value)} onBlur={() => blur("author_email")} style={hasErr("author_email")} />
          </FW>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
            <FW name="category" label="Research Field" required>
              <select id="category" className="field" value={form.category} onChange={(e) => set("category", e.target.value)} onBlur={() => blur("category")} style={hasErr("category")}>
                <option value="">Select a category…</option>
                {CATEGORY_LIST.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </FW>
            <FW name="keywords" label="Keywords" hint="4–8 terms, comma-separated">
              <input id="keywords" className="field" placeholder="e.g. machine learning, health, policy" value={form.keywords} onChange={(e) => set("keywords", e.target.value)} />
            </FW>
          </div>
          <FW name="abstract" label="Structured Abstract" required>
            <textarea id="abstract" rows="7" className="field" placeholder="Background · Objectives · Methods · Results · Conclusions (min. 100 characters)" value={form.abstract} onChange={(e) => set("abstract", e.target.value)} onBlur={() => blur("abstract")} style={{ ...hasErr("abstract"), resize: "vertical" }} />
            <div style={{ fontFamily: "var(--mono)", fontSize: "10px", color: form.abstract.length >= 100 ? "#16A34A" : "var(--ink-40)", marginTop: "5px", letterSpacing: "0.05em", textAlign: "right" }}>
              {form.abstract.length} / 100 min characters
            </div>
          </FW>
          <FW name="file" label="Manuscript File" hint="PDF or DOCX · max 25 MB">
            <Dropzone file={file} onFile={setFile} error={false} />
          </FW>
          <FW name="cover_letter" label="Cover Letter" hint="optional">
            <textarea id="cover_letter" rows="4" className="field" placeholder="Briefly describe the significance..." value={form.cover_letter} onChange={(e) => set("cover_letter", e.target.value)} style={{ resize: "vertical" }} />
          </FW>
          <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", padding: "16px 20px" }}>
            <div className="label" style={{ color: "#92400E", marginBottom: "8px" }}>Before Submitting</div>
            <ul style={{ fontSize: "13px", color: "#78350F", lineHeight: "1.8", paddingLeft: "16px" }}>
              <li>Ensure your manuscript is anonymized for blind review</li>
              <li>References must follow APA 7th edition format</li>
              <li>Maximum 12,000 words excluding references</li>
              <li>File must be PDF or DOCX, max 25 MB</li>
            </ul>
          </div>
          <button onClick={send} disabled={loading} className="btn btn-primary" style={{ height: "52px", fontSize: "12px", opacity: loading ? 0.7 : 1 }}>
            {loading ? (
              <span style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span style={{ width: "14px", height: "14px", border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "white", borderRadius: "50%", animation: "spinDot 0.7s linear infinite", display: "block" }} />
                Submitting...
              </span>
            ) : "Send to Editorial Board →"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   ARCHIVE PAGE (unchanged)
   ───────────────────────────────────────────────────────────────────────── */
function ArchivePage({ navigate }) {
  const [articles, setArticles] = useState(() => getCached("articles:all:100") || getCached("articles:all:50") || getCached("articles:all:10") || []);
  const [loading, setLoading] = useState(() => articles.length === 0);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState(null);
  const [filterVolume, setFilterVolume] = useState(null);
  const [filterIssue, setFilterIssue] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const cached = getCached("articles:all:100");
    if (cached && cached.length > 0) {
      setArticles(cached);
      setLoading(false);
    }
    fetchArticles({ limit: 100 }).then((rows) => {
      if (isMounted && rows) {
        setArticles(rows);
        setLoading(false);
      }
    });
    return () => { isMounted = false; };
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return articles.filter((a) => {
      const matchSearch =
        !q ||
        a.title?.toLowerCase().includes(q) ||
        a.authors?.toLowerCase().includes(q) ||
        a.abstract?.toLowerCase().includes(q);
      const matchCat = !filter || a.category === filter;
      const matchVolIssue =
        (!filterVolume && !filterIssue) ||
        (a.volume === filterVolume && a.issue === filterIssue);
      return matchSearch && matchCat && matchVolIssue;
    });
  }, [articles, search, filter, filterVolume, filterIssue]);

  const clearVolumeIssueFilter = () => { setFilterVolume(null); setFilterIssue(null); };
  const hasActiveFilters = filter || search || filterVolume || filterIssue;
  return (
    <div style={{ padding: "60px 0 100px" }}>
      <div className="container">
        <div className="animate-up" style={{ marginBottom: "40px" }}>
          <div className="label" style={{ color: "var(--ink-40)", marginBottom: "12px" }}>Research Repository</div>
          <h1 className="display-lg" style={{ marginBottom: "14px" }}>Archive</h1>
          <p style={{ color: "var(--ink-70)" }}>Browse all published research across all disciplines.</p>
        </div>
        <div className="animate-up animate-up-d1" style={{ display: "flex", gap: "12px", marginBottom: "36px", flexWrap: "wrap" }}>
          <div style={{ position: "relative", flex: "1", minWidth: "220px" }}>
            <svg style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "var(--ink-40)" }} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
            <input className="field" placeholder="Search titles, authors, abstracts…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ paddingLeft: "40px" }} />
          </div>
          <select className="field" value={filter || ""} onChange={(e) => setFilter(e.target.value || null)} style={{ width: "200px", flexShrink: 0 }}>
            <option value="">All Categories</option>
            {CATEGORY_LIST.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          {hasActiveFilters && (
            <button onClick={() => { setSearch(""); setFilter(null); clearVolumeIssueFilter(); }} className="btn btn-sm btn-ghost" style={{ whiteSpace: "nowrap" }}>
              Clear All Filters
            </button>
          )}
        </div>
        {(filterVolume || filterIssue) && (
          <div className="animate-up" style={{ marginBottom: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
              <span style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "var(--ink-40)", letterSpacing: "0.05em" }}>Currently showing: Volume {filterVolume}, Issue {filterIssue}</span>
              <button onClick={clearVolumeIssueFilter} className="btn btn-sm btn-ghost" style={{ padding: "4px 12px", fontSize: "9px" }}>Clear ×</button>
            </div>
          </div>
        )}
        <div className="rule-heavy" style={{ marginBottom: "8px" }} />
        <div className="label" style={{ color: "var(--ink-40)", marginBottom: "32px" }}>
          {loading ? "Loading…" : `${filtered.length} article${filtered.length !== 1 ? "s" : ""} found`}
        </div>
        {loading ? (
          <div style={{ display: "grid", gap: "1px", background: "var(--ink-15)" }}>
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} style={{ background: "white", padding: "20px 24px" }}>
                <div className="skeleton" style={{ width: "80px", height: "18px", marginBottom: "10px" }} />
                <div className="skeleton" style={{ width: "70%", height: "20px", marginBottom: "8px" }} />
                <div className="skeleton" style={{ width: "40%", height: "14px" }} />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <div style={{ fontSize: "48px", opacity: 0.15 }}>∅</div>
            <p style={{ color: "var(--ink-40)", fontStyle: "italic" }}>No results match your search.</p>
          </div>
        ) : (
          <div style={{ display: "grid", gap: "1px", background: "var(--ink-07)" }}>
            {filtered.map((a, i) => {
              const catColor = CATEGORY_COLORS[a.category] || "#666";
              return (
                <div key={a.id} className="animate-up" style={{ animationDelay: `${Math.min(i, 8) * 0.04}s`, background: "white", padding: "20px 24px", cursor: "pointer", transition: "background 0.15s" }} onClick={() => navigate("article", a.id)}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: "16px" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px", flexWrap: "wrap" }}>
                        <span className="cat-pill" style={{ background: `${catColor}15`, color: catColor }}>{a.category}</span>
                        {a.published_at && <span style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "var(--ink-40)" }}>{new Date(a.published_at).toLocaleDateString("en-US", { year: "numeric", month: "short" })}</span>}
                      </div>
                      <h3 style={{ fontFamily: "var(--serif-display)", fontSize: "17px", fontWeight: 600, lineHeight: 1.35, marginBottom: "6px", color: "var(--ink)" }}>{a.title}</h3>
                      {(a.volume || a.issue || getManuscriptDoi(a)) && (
                        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "6px", alignItems: "center" }}>
                          {a.volume && a.issue && (
                            <button onClick={(e) => { e.stopPropagation(); setFilterVolume(a.volume); setFilterIssue(a.issue); }} style={{ background: "var(--ink-07)", border: "none", borderRadius: "20px", padding: "2px 10px", fontFamily: "var(--mono)", fontSize: "9px", fontWeight: 500, letterSpacing: "0.06em", color: "var(--ink-70)", cursor: "pointer" }}>
                              Vol. {a.volume}, No. {a.issue}
                            </button>
                          )}
                          {a.volume && !a.issue && (
                            <button onClick={(e) => { e.stopPropagation(); setFilterVolume(a.volume); setFilterIssue(null); }} style={{ background: "var(--ink-07)", border: "none", borderRadius: "20px", padding: "2px 10px", fontFamily: "var(--mono)", fontSize: "9px", fontWeight: 500, letterSpacing: "0.06em", color: "var(--ink-70)", cursor: "pointer" }}>
                              Vol. {a.volume}
                            </button>
                          )}
                          {getManuscriptDoi(a) && (
                            <a
                              href={getManuscriptDoi(a)}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: "20px", padding: "2px 10px", fontFamily: "var(--mono)", fontSize: "9px", fontWeight: 600, letterSpacing: "0.04em", color: "var(--scarlet)", textDecoration: "none" }}
                            >
                              DOI: {getDoiDisplay(getManuscriptDoi(a))}
                            </a>
                          )}
                        </div>
                      )}
                      <p style={{ fontFamily: "var(--serif-body)", fontStyle: "italic", fontSize: "14px", color: "var(--ink-40)" }}>{a.authors}</p>
                    </div>
                    <svg style={{ flexShrink: 0, color: "var(--ink-15)", marginTop: "4px" }} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="9,18 15,12 9,6" /></svg>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   APC PAGE (unchanged)
   ───────────────────────────────────────────────────────────────────────── */
function APCPage({ navigate }) {
  return (
    <div>
      <div style={{ background: "var(--cream)", borderBottom: "1px solid var(--ink-15)", padding: "60px 0 56px" }}>
        <div className="container">
          <div className="label animate-up" style={{ color: "var(--ink-40)", marginBottom: "14px" }}>Open Access Funding & Equity</div>
          <h1 className="display-lg animate-up animate-up-d1" style={{ marginBottom: "14px" }}>Article Processing Charges (APC)</h1>
          <p className="animate-up animate-up-d2" style={{ color: "var(--ink-70)", fontSize: "17px", maxWidth: "640px", lineHeight: 1.7 }}>
            ScienceXplorer operates an equitable open-access model. Article Processing Charges are determined on a <strong>case-by-case basis</strong> upon manuscript acceptance to ensure that financial circumstances never prevent valid scholarship from being published.
          </p>
        </div>
      </div>

      <div className="container" style={{ padding: "60px 24px", maxWidth: "860px" }}>
        <div className="animate-up" style={{ marginBottom: "48px" }}>
          <div className="label" style={{ color: "var(--ink-40)", marginBottom: "12px" }}>Transparent & Author-First</div>
          <div className="rule-heavy" style={{ marginBottom: "24px" }} />
          <div style={{ background: "white", border: "1px solid var(--ink-15)", padding: "36px 32px", textAlign: "center", marginBottom: "32px" }}>
            <div className="label" style={{ color: "var(--scarlet)", marginBottom: "8px" }}>Pricing Model</div>
            <div style={{ fontFamily: "var(--serif-display)", fontSize: "40px", fontWeight: 900, color: "var(--ink)", lineHeight: 1.15 }}>
              Case-by-Case Evaluation
            </div>
            <div style={{ fontFamily: "var(--mono)", fontSize: "12px", color: "var(--ink-70)", letterSpacing: "0.06em", marginTop: "12px" }}>
              Assessed upon acceptance · Waivers & discounts available for all eligible authors
            </div>
            <div className="rule" style={{ margin: "24px 0" }} />
            <p style={{ fontSize: "15px", lineHeight: "1.75", color: "var(--ink-70)", maxWidth: "640px", margin: "0 auto" }}>
              There are <strong>zero submission fees</strong> and <strong>zero review charges</strong>. APCs are only assessed after a manuscript has passed double-blind peer review and been formally accepted. Authors in need of financial assistance receive customized subsidies or complete fee waivers.
            </p>
          </div>

          <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", padding: "20px 24px", marginBottom: "28px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px" }}>
            <div>
              <div style={{ fontFamily: "var(--mono)", fontSize: "11px", fontWeight: 700, color: "#991B1B", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "4px" }}>
                Important Waiver Notice
              </div>
              <p style={{ fontSize: "14px", color: "#7F1D1D", lineHeight: 1.6 }}>
                APC waivers are <strong>strictly provided only to authors affiliated with institutions in designated low-income countries</strong>. Authors from middle-income (e.g. Nigeria) or high-income nations are not eligible.
              </p>
            </div>
            <button
              onClick={() => navigate("apcwaiver")}
              className="btn btn-primary btn-sm"
              style={{ flexShrink: 0 }}
            >
              View 20 Eligible Countries →
            </button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "24px", marginTop: "32px" }}>
            <div style={{ background: "var(--cream)", padding: "26px", border: "1px solid var(--ink-07)" }}>
              <div style={{ fontSize: "28px", marginBottom: "12px" }}>🌍</div>
              <h3 style={{ fontFamily: "var(--serif-display)", fontSize: "18px", fontWeight: 700, marginBottom: "8px" }}>Low-Income Country Waivers</h3>
              <p style={{ fontSize: "14px", lineHeight: "1.75", color: "var(--ink-70)", marginBottom: "14px" }}>
                Full 100% APC waivers are exclusively granted to authors whose primary institutional affiliation is situated in low-income economies. Authors from middle-income or high-income nations are not eligible.
              </p>
              <button
                onClick={() => navigate("apcwaiver")}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "var(--scarlet)",
                  fontFamily: "var(--mono)",
                  fontSize: "11px",
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  padding: 0,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                View 20 Eligible Nations →
              </button>
            </div>
            <div style={{ background: "var(--cream)", padding: "26px", border: "1px solid var(--ink-07)" }}>
              <div style={{ fontSize: "28px", marginBottom: "12px" }}>🎓</div>
              <h3 style={{ fontFamily: "var(--serif-display)", fontSize: "18px", fontWeight: 700, marginBottom: "8px" }}>Students & Early Career</h3>
              <p style={{ fontSize: "14px", lineHeight: "1.75", color: "var(--ink-70)" }}>
                Independent researchers, postgraduate students, postdocs, and early-career scholars without grant backing are eligible for substantial fee reductions or full hardship waivers upon request.
              </p>
            </div>
            <div style={{ background: "var(--cream)", padding: "26px", border: "1px solid var(--ink-07)" }}>
              <div style={{ fontSize: "28px", marginBottom: "12px" }}>🏛️</div>
              <h3 style={{ fontFamily: "var(--serif-display)", fontSize: "18px", fontWeight: 700, marginBottom: "8px" }}>Funded & Institutional</h3>
              <p style={{ fontSize: "14px", lineHeight: "1.75", color: "var(--ink-70)" }}>
                When authors hold active grant funding or access to institutional open-access budgets, standard publishing contributions are billed directly to the sponsor or university library fund.
              </p>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "24px", marginTop: "24px" }}>
            <div style={{ background: "var(--cream)", padding: "26px", border: "1px solid var(--ink-07)" }}>
              <div style={{ fontSize: "28px", marginBottom: "12px" }}>📄</div>
              <h3 style={{ fontFamily: "var(--serif-display)", fontSize: "18px", fontWeight: 700, marginBottom: "8px" }}>What the APC Covers</h3>
              <ul style={{ paddingLeft: "18px", fontSize: "14px", lineHeight: "1.8", color: "var(--ink-70)" }}>
                <li>Professional typesetting & layout formatting</li>
                <li>Permanent digital archiving & persistent links</li>
                <li>Global open-access dissemination (CC-BY 4.0)</li>
                <li>Double-blind peer review coordination</li>
                <li>Editorial handling and publication infrastructure</li>
              </ul>
            </div>
            <div style={{ background: "var(--cream)", padding: "26px", border: "1px solid var(--ink-07)" }}>
              <div style={{ fontSize: "28px", marginBottom: "12px" }}>🛡️</div>
              <h3 style={{ fontFamily: "var(--serif-display)", fontSize: "18px", fontWeight: 700, marginBottom: "8px" }}>Editorial Independence</h3>
              <p style={{ fontSize: "14px", lineHeight: "1.75", color: "var(--ink-70)" }}>
                Editorial decisions are strictly separated from financial discussions. Reviewers and editors do not know whether an author has requested a fee waiver, guaranteeing impartial scientific evaluation.
              </p>
            </div>
            <div style={{ background: "var(--cream)", padding: "26px", border: "1px solid var(--ink-07)" }}>
              <div style={{ fontSize: "28px", marginBottom: "12px" }}>💬</div>
              <h3 style={{ fontFamily: "var(--serif-display)", fontSize: "18px", fontWeight: 700, marginBottom: "8px" }}>How to Request Support</h3>
              <p style={{ fontSize: "14px", lineHeight: "1.75", color: "var(--ink-70)" }}>
                You can note your waiver or fee assistance request in your submission cover letter, or discuss your situation directly with the editorial office once acceptance has been confirmed.
              </p>
            </div>
          </div>
        </div>

        <div className="animate-up animate-up-d1" style={{ background: "#F0FDF4", border: "1px solid #86EFAC", padding: "32px", marginTop: "24px" }}>
          <div className="label" style={{ color: "#14532D", marginBottom: "12px" }}>No Surprise Fees — Our Guarantee</div>
          <p style={{ fontSize: "15px", lineHeight: "1.7", color: "#166534", marginBottom: "16px" }}>
            ScienceXplorer is committed to ethical, transparent publishing. We never charge for:
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
            {["Initial Submission", "Peer Review Process", "Color Figures & Media", "Word Count / Page Charges", "Revisions & Formatting", "Data Supplements"].map((item) => (
              <div key={item} style={{ background: "white", padding: "8px 16px", borderRadius: "40px", fontSize: "13px", fontFamily: "var(--mono)", color: "#065F46", letterSpacing: "0.05em", border: "1px solid #BBF7D0" }}>
                ✓ {item}
              </div>
            ))}
          </div>
        </div>

        <div className="animate-up animate-up-d2" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "24px", marginTop: "48px", padding: "28px 32px", border: "1px solid var(--ink-15)", background: "white" }}>
          <div>
            <div style={{ fontFamily: "var(--serif-display)", fontSize: "18px", fontWeight: 700, marginBottom: "6px" }}>Ready to publish your research?</div>
            <p style={{ fontSize: "14px", color: "var(--ink-70)" }}>Submission is 100% free with no upfront cost. Fees are addressed on a case-by-case basis after acceptance.</p>
          </div>
          <button onClick={() => navigate("submit")} className="btn btn-primary">Submit Manuscript →</button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   APC WAIVER PAGE (/APCwaiver)
   ───────────────────────────────────────────────────────────────────────── */
const LOW_INCOME_COUNTRIES = [
  { id: 1, name: "Afghanistan", code: "AF", region: "South Asia", incomeGroup: "Low-Income Economy (World Bank)", waiver: "100% Full Waiver" },
  { id: 2, name: "Burkina Faso", code: "BF", region: "West Africa", incomeGroup: "Low-Income Economy (World Bank)", waiver: "100% Full Waiver" },
  { id: 3, name: "Burundi", code: "BI", region: "East Africa", incomeGroup: "Low-Income Economy (World Bank)", waiver: "100% Full Waiver" },
  { id: 4, name: "Central African Republic", code: "CF", region: "Central Africa", incomeGroup: "Low-Income Economy (World Bank)", waiver: "100% Full Waiver" },
  { id: 5, name: "Chad", code: "TD", region: "Central Africa", incomeGroup: "Low-Income Economy (World Bank)", waiver: "100% Full Waiver" },
  { id: 6, name: "Democratic Republic of the Congo", code: "CD", region: "Central Africa", incomeGroup: "Low-Income Economy (World Bank)", waiver: "100% Full Waiver" },
  { id: 7, name: "Eritrea", code: "ER", region: "East Africa", incomeGroup: "Low-Income Economy (World Bank)", waiver: "100% Full Waiver" },
  { id: 8, name: "Ethiopia", code: "ET", region: "East Africa", incomeGroup: "Low-Income Economy (World Bank)", waiver: "100% Full Waiver" },
  { id: 9, name: "The Gambia", code: "GM", region: "West Africa", incomeGroup: "Low-Income Economy (World Bank)", waiver: "100% Full Waiver" },
  { id: 10, name: "Guinea-Bissau", code: "GW", region: "West Africa", incomeGroup: "Low-Income Economy (World Bank)", waiver: "100% Full Waiver" },
  { id: 11, name: "Liberia", code: "LR", region: "West Africa", incomeGroup: "Low-Income Economy (World Bank)", waiver: "100% Full Waiver" },
  { id: 12, name: "Madagascar", code: "MG", region: "East Africa", incomeGroup: "Low-Income Economy (World Bank)", waiver: "100% Full Waiver" },
  { id: 13, name: "Malawi", code: "MW", region: "Southern Africa", incomeGroup: "Low-Income Economy (World Bank)", waiver: "100% Full Waiver" },
  { id: 14, name: "Mali", code: "ML", region: "West Africa", incomeGroup: "Low-Income Economy (World Bank)", waiver: "100% Full Waiver" },
  { id: 15, name: "Mozambique", code: "MZ", region: "Southern Africa", incomeGroup: "Low-Income Economy (World Bank)", waiver: "100% Full Waiver" },
  { id: 16, name: "Niger", code: "NE", region: "West Africa", incomeGroup: "Low-Income Economy (World Bank)", waiver: "100% Full Waiver" },
  { id: 17, name: "Rwanda", code: "RW", region: "East Africa", incomeGroup: "Low-Income Economy (World Bank)", waiver: "100% Full Waiver" },
  { id: 18, name: "Sierra Leone", code: "SL", region: "West Africa", incomeGroup: "Low-Income Economy (World Bank)", waiver: "100% Full Waiver" },
  { id: 19, name: "Somalia", code: "SO", region: "East Africa", incomeGroup: "Low-Income Economy (World Bank)", waiver: "100% Full Waiver" },
  { id: 20, name: "South Sudan", code: "SS", region: "East Africa", incomeGroup: "Low-Income Economy (World Bank)", waiver: "100% Full Waiver" },
];

function APCWaiverPage({ navigate }) {
  const [search, setSearch] = useState("");
  const [regionFilter, setRegionFilter] = useState("all");

  const regions = ["all", "West Africa", "East Africa", "Central Africa", "Southern Africa", "South Asia"];

  const filteredCountries = useMemo(() => {
    const q = search.toLowerCase().trim();
    return LOW_INCOME_COUNTRIES.filter((c) => {
      const matchQuery = !q || c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q) || c.region.toLowerCase().includes(q);
      const matchRegion = regionFilter === "all" || c.region === regionFilter;
      return matchQuery && matchRegion;
    });
  }, [search, regionFilter]);

  const searchedIsNigeria = search.toLowerCase().trim().includes("nigeria");

  return (
    <div>
      {/* Header */}
      <div style={{ background: "var(--cream)", borderBottom: "1px solid var(--ink-15)", padding: "60px 0 56px" }}>
        <div className="container">
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
            <button onClick={() => navigate("apc")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--scarlet)", fontFamily: "var(--mono)", fontSize: "11px", letterSpacing: "0.08em", textTransform: "uppercase", padding: 0 }}>
              ← APC Overview
            </button>
            <span style={{ color: "var(--ink-40)" }}>/</span>
            <span style={{ fontFamily: "var(--mono)", fontSize: "11px", color: "var(--ink-70)", letterSpacing: "0.08em", textTransform: "uppercase" }}>Waiver Eligibility</span>
          </div>
          <h1 className="display-lg animate-up" style={{ marginBottom: "16px" }}>
            Article Processing Charge (APC) Waiver Policy
          </h1>
          <p className="animate-up animate-up-d1" style={{ color: "var(--ink-70)", fontSize: "17px", maxWidth: "720px", lineHeight: 1.75 }}>
            ScienceXplorer is committed to equity in academic publishing. In order to safeguard sustainable journal operations, <strong>APC waivers are exclusively and strictly provided to authors whose primary institutional affiliation is situated in low-income countries</strong>.
          </p>
        </div>
      </div>

      <div className="container" style={{ padding: "52px 24px 72px", maxWidth: "960px" }}>
        {/* Core Policy Rule */}
        <div className="animate-up" style={{ background: "white", border: "1px solid var(--ink-15)", padding: "32px 36px", marginBottom: "36px" }}>
          <div className="label" style={{ color: "var(--scarlet)", marginBottom: "8px" }}>Institutional Equity Rule</div>
          <h2 style={{ fontFamily: "var(--serif-display)", fontSize: "28px", fontWeight: 700, color: "var(--ink)", marginBottom: "14px", lineHeight: 1.3 }}>
            Waivers are Granted Only to Low-Income Countries
          </h2>
          <p style={{ fontSize: "15px", lineHeight: 1.8, color: "var(--ink-70)", marginBottom: "20px" }}>
            Authors submitting manuscripts from <strong>recognized low-income economies</strong> are eligible for a <strong>100% full fee waiver</strong> upon manuscript acceptance. All submission and peer-review processes remain completely free of charge.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px", background: "var(--cream)", padding: "20px 24px", border: "1px solid var(--ink-07)" }}>
            <div>
              <div style={{ fontFamily: "var(--mono)", fontSize: "11px", color: "var(--ink-40)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Waiver Benefit</div>
              <div style={{ fontFamily: "var(--serif-display)", fontSize: "20px", fontWeight: 700, color: "var(--scarlet)", marginTop: "4px" }}>100% Full Fee Waiver</div>
            </div>
            <div>
              <div style={{ fontFamily: "var(--mono)", fontSize: "11px", color: "var(--ink-40)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Eligible Nations</div>
              <div style={{ fontFamily: "var(--serif-display)", fontSize: "20px", fontWeight: 700, color: "var(--ink)", marginTop: "4px" }}>20 Listed Countries</div>
            </div>
            <div>
              <div style={{ fontFamily: "var(--mono)", fontSize: "11px", color: "var(--ink-40)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Classification Standard</div>
              <div style={{ fontFamily: "var(--serif-display)", fontSize: "20px", fontWeight: 700, color: "var(--ink)", marginTop: "4px" }}>World Bank Low-Income</div>
            </div>
          </div>
        </div>

        {/* Explicit Exclusion Callout */}
        <div className="animate-up animate-up-d1" style={{ background: "#FEF2F2", border: "1px solid #F87171", padding: "24px 28px", marginBottom: "40px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: "16px" }}>
            <div style={{ fontSize: "24px", lineHeight: 1 }}>⚠️</div>
            <div>
              <div style={{ fontFamily: "var(--serif-display)", fontSize: "17px", fontWeight: 700, color: "#991B1B", marginBottom: "8px" }}>
                Strict Country Eligibility & Exclusion Notice
              </div>
              <p style={{ fontSize: "14px", lineHeight: "1.8", color: "#7F1D1D" }}>
                Under journal governance policies, APC fee waivers are <strong>strictly not available to authors from middle-income or high-income nations</strong>.
                Specifically, middle-income economies—including <strong>Nigeria</strong>, Ghana, Kenya, Egypt, India, Pakistan, South Africa, and all countries not listed among the 20 approved low-income nations below—are <strong>ineligible for APC fee waivers</strong>.
                Manuscripts originating from these countries are evaluated under the standard case-by-case APC assessment upon acceptance.
              </p>
            </div>
          </div>
        </div>

        {/* Interactive Search & List */}
        <div className="animate-up animate-up-d2" style={{ marginBottom: "48px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: "12px", marginBottom: "16px" }}>
            <div>
              <div className="label" style={{ color: "var(--ink-40)", marginBottom: "4px" }}>Country Directory</div>
              <h2 style={{ fontFamily: "var(--serif-display)", fontSize: "24px", fontWeight: 700, color: "var(--ink)" }}>
                20 Eligible Low-Income Countries
              </h2>
            </div>
            <div style={{ fontFamily: "var(--mono)", fontSize: "12px", color: "var(--ink-70)" }}>
              Showing {filteredCountries.length} of 20 countries
            </div>
          </div>

          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "20px" }}>
            <input
              type="text"
              placeholder="Search by country name, ISO code, or region..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                flex: "1 1 280px",
                padding: "12px 16px",
                border: "1px solid var(--ink-15)",
                fontFamily: "var(--serif-body)",
                fontSize: "15px",
                outline: "none",
                background: "white",
              }}
            />
            <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", alignItems: "center" }}>
              {regions.map((reg) => (
                <button
                  key={reg}
                  onClick={() => setRegionFilter(reg)}
                  style={{
                    padding: "8px 14px",
                    background: regionFilter === reg ? "var(--ink)" : "white",
                    color: regionFilter === reg ? "white" : "var(--ink-70)",
                    border: "1px solid var(--ink-15)",
                    fontFamily: "var(--mono)",
                    fontSize: "11px",
                    cursor: "pointer",
                    textTransform: "capitalize",
                    transition: "all 0.15s",
                  }}
                >
                  {reg === "all" ? "All Regions" : reg}
                </button>
              ))}
            </div>
          </div>

          {/* Specific feedback for Nigeria search */}
          {searchedIsNigeria && (
            <div style={{ background: "#FEF9C3", border: "1px solid #FDE68A", padding: "16px 20px", marginBottom: "20px" }}>
              <strong style={{ fontFamily: "var(--serif-display)", fontSize: "15px", color: "#854D0E", display: "block", marginBottom: "4px" }}>
                Nigeria Status: Ineligible for APC Waiver
              </strong>
              <p style={{ fontSize: "13px", color: "#713F12", lineHeight: 1.6 }}>
                Nigeria is categorized as a lower-middle-income economy by international standards. APC waivers are strictly limited to low-income countries. Authors from Nigeria follow our standard case-by-case APC evaluation.
              </p>
            </div>
          )}

          {/* 20 Countries Grid */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(270px, 1fr))", gap: "16px" }}>
            {filteredCountries.map((c) => (
              <div
                key={c.id}
                style={{
                  background: "white",
                  border: "1px solid var(--ink-15)",
                  padding: "20px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  transition: "border-color 0.2s, transform 0.2s",
                }}
              >
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                    <span style={{ fontFamily: "var(--mono)", fontSize: "11px", color: "var(--scarlet)", fontWeight: 700 }}>
                      #{String(c.id).padStart(2, "0")}
                    </span>
                    <span style={{ fontFamily: "var(--mono)", fontSize: "10px", background: "var(--cream)", padding: "2px 8px", border: "1px solid var(--ink-15)", color: "var(--ink-70)" }}>
                      {c.code}
                    </span>
                  </div>
                  <h3 style={{ fontFamily: "var(--serif-display)", fontSize: "19px", fontWeight: 700, color: "var(--ink)", marginBottom: "4px" }}>
                    {c.name}
                  </h3>
                  <div style={{ fontFamily: "var(--mono)", fontSize: "11px", color: "var(--ink-40)", marginBottom: "12px" }}>
                    {c.region}
                  </div>
                </div>
                <div style={{ borderTop: "1px solid var(--ink-07)", paddingTop: "12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "var(--ink-70)" }}>
                    World Bank Low-Income
                  </span>
                  <span style={{ background: "#DCFCE7", color: "#14532D", fontFamily: "var(--mono)", fontSize: "10px", padding: "2px 8px", borderRadius: "100px", fontWeight: 600 }}>
                    100% Waiver
                  </span>
                </div>
              </div>
            ))}
          </div>

          {filteredCountries.length === 0 && !searchedIsNigeria && (
            <div style={{ background: "white", border: "1px solid var(--ink-15)", padding: "40px", textAlign: "center", color: "var(--ink-70)" }}>
              <div style={{ fontSize: "28px", marginBottom: "8px" }}>🔍</div>
              <p style={{ fontFamily: "var(--serif-display)", fontSize: "18px", color: "var(--ink)", marginBottom: "6px" }}>
                No eligible low-income country found matching "{search}"
              </p>
              <p style={{ fontSize: "14px" }}>
                Only the 20 officially designated low-income countries qualify for APC fee waivers. Middle- and high-income countries are not eligible.
              </p>
            </div>
          )}
        </div>

        {/* Verification and Application Protocol */}
        <div className="animate-up" style={{ background: "var(--cream)", border: "1px solid var(--ink-15)", padding: "32px 36px", marginBottom: "40px" }}>
          <div className="label" style={{ color: "var(--ink-40)", marginBottom: "8px" }}>Application & Verification</div>
          <h2 style={{ fontFamily: "var(--serif-display)", fontSize: "22px", fontWeight: 700, color: "var(--ink)", marginBottom: "16px" }}>
            Mandatory Eligibility Criteria & Application Procedure
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "20px" }}>
            <div>
              <div style={{ fontFamily: "var(--mono)", fontSize: "11px", color: "var(--scarlet)", fontWeight: 700, marginBottom: "4px" }}>Criterion 1</div>
              <h4 style={{ fontFamily: "var(--serif-display)", fontSize: "16px", fontWeight: 700, marginBottom: "6px" }}>Primary Affiliation</h4>
              <p style={{ fontSize: "13px", lineHeight: 1.7, color: "var(--ink-70)" }}>
                The corresponding author must be actively employed by, or enrolled in, an accredited higher education institution or research body physically located in one of the 20 countries.
              </p>
            </div>
            <div>
              <div style={{ fontFamily: "var(--mono)", fontSize: "11px", color: "var(--scarlet)", fontWeight: 700, marginBottom: "4px" }}>Criterion 2</div>
              <h4 style={{ fontFamily: "var(--serif-display)", fontSize: "16px", fontWeight: 700, marginBottom: "6px" }}>In-Country Research</h4>
              <p style={{ fontSize: "13px", lineHeight: 1.7, color: "var(--ink-70)" }}>
                The primary fieldwork, theoretical derivation, or laboratory experiments must have been conducted within the eligible jurisdiction without non-qualifying external commercial funding.
              </p>
            </div>
            <div>
              <div style={{ fontFamily: "var(--mono)", fontSize: "11px", color: "var(--scarlet)", fontWeight: 700, marginBottom: "4px" }}>Criterion 3</div>
              <h4 style={{ fontFamily: "var(--serif-display)", fontSize: "16px", fontWeight: 700, marginBottom: "6px" }}>No International Grants</h4>
              <p style={{ fontSize: "13px", lineHeight: 1.7, color: "var(--ink-70)" }}>
                Manuscripts co-authored with researchers from middle-income or high-income institutions that possess APC budgets are not eligible for the low-income fee waiver.
              </p>
            </div>
            <div>
              <div style={{ fontFamily: "var(--mono)", fontSize: "11px", color: "var(--scarlet)", fontWeight: 700, marginBottom: "4px" }}>Criterion 4</div>
              <h4 style={{ fontFamily: "var(--serif-display)", fontSize: "16px", fontWeight: 700, marginBottom: "6px" }}>Cover Letter Declaration</h4>
              <p style={{ fontSize: "13px", lineHeight: 1.7, color: "var(--ink-70)" }}>
                Authors must formally request the low-income waiver in their submission cover letter, confirming their institutional affiliation and country eligibility.
              </p>
            </div>
          </div>
        </div>

        {/* CTA Footer Card */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "20px", padding: "28px 32px", background: "white", border: "1px solid var(--ink-15)" }}>
          <div>
            <div style={{ fontFamily: "var(--serif-display)", fontSize: "18px", fontWeight: 700, marginBottom: "4px" }}>
              Ready to submit your research?
            </div>
            <p style={{ fontSize: "14px", color: "var(--ink-70)" }}>
              Manuscripts from all disciplines undergo rigorous double-blind peer review. Initial submission is 100% free.
            </p>
          </div>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <button onClick={() => navigate("apc")} className="btn btn-outline">
              APC Policy Overview
            </button>
            <button onClick={() => navigate("submit")} className="btn btn-primary">
              Submit Manuscript →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   PUBLISH FORM (with Volume, Issue, Publication Date, and DOI Link)
   ───────────────────────────────────────────────────────────────────────── */
function PublishForm({ manuscript, onPublish }) {
  const [open, setOpen] = useState(false);
  const [volume, setVolume] = useState(manuscript.volume || "");
  const [issue, setIssue] = useState(manuscript.issue || "");
  const [pubDate, setPubDate] = useState(() => manuscript.published_at ? manuscript.published_at.slice(0, 10) : "");
  const [doiLink, setDoiLink] = useState(() => getManuscriptDoi(manuscript) || "");
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});

  const isPublished = manuscript.status === "published";

  const validate = () => {
    const e = {};
    if (!String(volume).trim()) e.volume = "Required";
    if (!String(issue).trim()) e.issue = "Required";
    if (!pubDate) e.pubDate = "Required";
    if (doiLink.trim() && !doiLink.includes("10.") && !doiLink.includes("doi.org")) {
      e.doiLink = "Please enter a valid DOI identifier or link (e.g. 10.xxxx/... or https://doi.org/...)";
    }
    return e;
  };

  const confirm = async () => {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setBusy(true);
    const dateObj = new Date(pubDate);
    dateObj.setUTCHours(12, 0, 0, 0);
    const cleanDoi = normalizeDoi(doiLink);
    await onPublish({
      volume: parseInt(volume),
      issue: parseInt(issue),
      pub_month: dateObj.getUTCMonth() + 1,
      pub_year: dateObj.getUTCFullYear(),
      published_at: dateObj.toISOString(),
      doi: cleanDoi,
    });
    setBusy(false);
    setOpen(false);
  };

  const iStyle = (name) => ({
    padding: "8px 10px",
    border: `1px solid ${errors[name] ? "var(--scarlet)" : "#DDD"}`,
    fontFamily: "var(--serif-body)",
    fontSize: "13px",
    width: "100%",
    outline: "none",
    boxSizing: "border-box",
    background: "white",
  });

  const LB = ({ name, label, required = true, children }) => (
    <div style={{ flex: 1 }}>
      <div style={{ fontFamily: "var(--mono)", fontSize: "9px", color: "#666", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "4px" }}>
        {label} {required && <span style={{ color: "var(--scarlet)" }}>*</span>}
      </div>
      {children}
      {errors[name] && <div style={{ fontFamily: "var(--mono)", fontSize: "9px", color: "var(--scarlet)", marginTop: "3px" }}>{errors[name]}</div>}
    </div>
  );

  if (!open)
    return (
      <button
        id={`publish-btn-${manuscript.id}`}
        onClick={() => {
          setVolume(manuscript.volume || "");
          setIssue(manuscript.issue || "");
          setPubDate(manuscript.published_at ? manuscript.published_at.slice(0, 10) : "");
          setDoiLink(getManuscriptDoi(manuscript) || "");
          setErrors({});
          setOpen(true);
        }}
        style={{
          padding: "11px",
          background: isPublished ? "#EFF6FF" : "#DCFCE7",
          color: isPublished ? "#1E40AF" : "#14532D",
          border: `1px solid ${isPublished ? "#BFDBFE" : "#86EFAC"}`,
          cursor: "pointer",
          fontFamily: "var(--mono)",
          fontSize: "11px",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          width: "100%",
          textAlign: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "6px",
        }}
      >
        {isPublished ? "📘 Edit Publication Details & DOI…" : "✓ Publish…"}
      </button>
    );

  return (
    <div id={`publish-form-${manuscript.id}`} style={{ background: isPublished ? "#F8FAFC" : "#F0FDF4", border: `1px solid ${isPublished ? "#CBD5E1" : "#86EFAC"}`, padding: "16px" }}>
      <div style={{ fontFamily: "var(--mono)", fontSize: "10px", color: isPublished ? "#1E293B" : "#166534", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span>📘 {isPublished ? "Update Publication Details & DOI" : "Publication Details"}</span>
        <button onClick={() => setOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "#999", fontSize: "16px", lineHeight: 1 }}>×</button>
      </div>
      <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
        <LB name="volume" label="Volume"><input id="pub-field-volume" type="number" min="1" placeholder="e.g. 8" value={volume} onChange={(e) => setVolume(e.target.value)} style={iStyle("volume")} /></LB>
        <LB name="issue" label="Issue"><input id="pub-field-issue" type="number" min="1" placeholder="e.g. 2" value={issue} onChange={(e) => setIssue(e.target.value)} style={iStyle("issue")} /></LB>
      </div>
      <div style={{ marginBottom: "10px" }}>
        <LB name="pubDate" label="Publication Date"><input id="pub-field-date" type="date" value={pubDate} onChange={(e) => setPubDate(e.target.value)} style={iStyle("pubDate")} /></LB>
      </div>
      <div style={{ marginBottom: "12px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
          <span style={{ fontFamily: "var(--mono)", fontSize: "9px", color: "#64748B", letterSpacing: "0.1em", textTransform: "uppercase" }}>
            DOI Link / Identifier
          </span>
          <span style={{ fontFamily: "var(--mono)", fontSize: "9px", color: "#94A3B8" }}>Optional</span>
        </div>
        <input
          id="pub-field-doi"
          type="text"
          placeholder="e.g. https://doi.org/10.62858/sx.2026.08.02 or 10.62858/..."
          value={doiLink}
          onChange={(e) => setDoiLink(e.target.value)}
          style={iStyle("doiLink")}
        />
        {errors.doiLink && (
          <div style={{ fontFamily: "var(--mono)", fontSize: "9px", color: "var(--scarlet)", marginTop: "3px" }}>
            {errors.doiLink}
          </div>
        )}
        <div style={{ fontFamily: "var(--mono)", fontSize: "9px", color: "#64748B", marginTop: "4px", lineHeight: 1.4 }}>
          Enter the permanent DOI URL or alphanumeric identifier (e.g. https://doi.org/10.xxxx/... or 10.xxxx/...).
        </div>
      </div>
      {(pubDate || doiLink) && (
        <div style={{ fontFamily: "var(--mono)", fontSize: "10px", color: isPublished ? "#1E3A8A" : "#166534", background: isPublished ? "#EFF6FF" : "#DCFCE7", padding: "8px 10px", margin: "10px 0", letterSpacing: "0.04em", display: "flex", flexDirection: "column", gap: "4px" }}>
          <div>Vol. {volume || "?"}, No. {issue || "?"} {pubDate && `· ${new Date(pubDate).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}`}</div>
          {doiLink && (
            <div style={{ wordBreak: "break-all", color: isPublished ? "#1D4ED8" : "#14532D" }}>
              DOI: {normalizeDoi(doiLink)}
            </div>
          )}
        </div>
      )}
      <div style={{ display: "flex", gap: "8px" }}>
        <button onClick={confirm} disabled={busy} style={{ flex: 1, padding: "10px", background: busy ? "#9CA3AF" : (isPublished ? "#1D4ED8" : "#166534"), color: "white", border: "none", cursor: busy ? "not-allowed" : "pointer", fontFamily: "var(--mono)", fontSize: "11px", letterSpacing: "0.08em", textTransform: "uppercase", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
          {busy ? <><span style={{ width: "11px", height: "11px", border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "white", borderRadius: "50%", animation: "spinDot 0.7s linear infinite", display: "block" }} />Saving…</> : (isPublished ? "✓ Save Publication & DOI" : "✓ Confirm & Publish")}
        </button>
        <button onClick={() => setOpen(false)} style={{ padding: "10px 14px", background: "white", border: "1px solid #DDD", cursor: "pointer", fontFamily: "var(--mono)", fontSize: "11px", color: "#666" }}>Cancel</button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   EDIT MANUSCRIPT MODAL (unchanged except alert -> window.alert)
   ───────────────────────────────────────────────────────────────────────── */
function EditManuscriptModal({ manuscript, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: manuscript.title || "",
    authors: manuscript.authors || "",
    affiliations: manuscript.affiliations || "",
    author_email: manuscript.author_email || "",
    category: manuscript.category || "",
    abstract: manuscript.abstract || "",
    keywords: manuscript.keywords || "",
    cover_letter: manuscript.cover_letter || "",
    doi: getManuscriptDoi(manuscript) || "",
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = "Required";
    if (!form.authors.trim()) e.authors = "Required";
    if (!form.affiliations.trim()) e.affiliations = "Required";
    if (!form.author_email.trim()) e.author_email = "Required";
    else if (!/^[^@]+@[^@]+\.[^@]+$/.test(form.author_email)) e.author_email = "Invalid email";
    if (!form.category) e.category = "Required";
    if (!form.abstract.trim()) e.abstract = "Required";
    if (form.doi && form.doi.trim() && !form.doi.includes("10.") && !form.doi.includes("doi.org")) {
      e.doi = "Please enter a valid DOI identifier or link (e.g. 10.xxxx/... or https://doi.org/...)";
    }
    return e;
  };
  const handleSave = async () => {
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setLoading(true);
    try {
      let pdf_url = manuscript.pdf_url;
      if (file) {
        pdf_url = await uploadPDF(file, form.title);
      }
      const cleanDoi = normalizeDoi(form.doi);
      await updateManuscript(manuscript.id, { ...form, doi: cleanDoi, pdf_url });
      onSaved();
      onClose();
    } catch (e) {
      window.alert(e.message);
    } finally {
      setLoading(false);
    }
  };
  const AF = ({ name, label, required, children }) => (
    <div style={{ display: "flex", flexDirection: "column", gap: "5px", marginBottom: "12px" }}>
      <label style={{ fontFamily: "var(--mono)", fontSize: "9px", color: "#999", letterSpacing: "0.1em", textTransform: "uppercase" }}>
        {label}{required && <span style={{ color: "var(--scarlet)" }}>*</span>}
      </label>
      {children}
      {errors[name] && <span style={{ fontFamily: "var(--mono)", fontSize: "9px", color: "var(--scarlet)" }}>{errors[name]}</span>}
    </div>
  );
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
      <div className="animate-up" style={{ background: "white", width: "100%", maxWidth: "700px", maxHeight: "90vh", overflowY: "auto", padding: "28px", borderRadius: "4px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <div style={{ fontFamily: "var(--serif-display)", fontSize: "20px", fontWeight: 700 }}>Edit Manuscript</div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: "24px", cursor: "pointer" }}>×</button>
        </div>
        <AF name="title" label="Title" required><input className="field" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} /></AF>
        <AF name="authors" label="Authors (semicolon‑separated)" required><input className="field" value={form.authors} onChange={(e) => setForm((f) => ({ ...f, authors: e.target.value }))} /></AF>
        <AF name="affiliations" label="Affiliations (semicolon‑separated)" required><input className="field" value={form.affiliations} onChange={(e) => setForm((f) => ({ ...f, affiliations: e.target.value }))} /></AF>
        <AF name="author_email" label="Corresponding Email" required><input className="field" type="email" value={form.author_email} onChange={(e) => setForm((f) => ({ ...f, author_email: e.target.value }))} /></AF>
        <AF name="category" label="Category" required>
          <select className="field" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
            <option value="">Select…</option>
            {CATEGORY_LIST.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </AF>
        <AF name="doi" label="DOI Link / Identifier (optional)">
          <input className="field" placeholder="e.g. https://doi.org/10.62858/sx.2026.08.02 or 10.62858/..." value={form.doi} onChange={(e) => setForm((f) => ({ ...f, doi: e.target.value }))} />
        </AF>
        <AF name="keywords" label="Keywords"><input className="field" value={form.keywords} onChange={(e) => setForm((f) => ({ ...f, keywords: e.target.value }))} /></AF>
        <AF name="abstract" label="Abstract" required><textarea rows="6" className="field" value={form.abstract} onChange={(e) => setForm((f) => ({ ...f, abstract: e.target.value }))} style={{ resize: "vertical" }} /></AF>
        <AF name="cover_letter" label="Cover Letter"><textarea rows="3" className="field" value={form.cover_letter} onChange={(e) => setForm((f) => ({ ...f, cover_letter: e.target.value }))} style={{ resize: "vertical" }} /></AF>
        <AF name="file" label="Replace PDF (optional)"><Dropzone file={file} onFile={setFile} error={false} /></AF>
        <div style={{ display: "flex", gap: "12px", marginTop: "16px" }}>
          <button onClick={handleSave} disabled={loading} className="btn btn-primary" style={{ flex: 1 }}>{loading ? "Saving..." : "Save Changes"}</button>
          <button onClick={onClose} className="btn btn-ghost">Cancel</button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   ADMIN DASHBOARD – includes "Resolve Previous Links" button
   ───────────────────────────────────────────────────────────────────────── */
const ADMIN_STATUS_TABS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "published", label: "Published" },
  { key: "rejected", label: "Rejected" },
];

function AdminDashboard({ onLogout, navigate }) {
  const [data, setData] = useState(() => getCached("manuscripts:all") || []);
  const [sel, setSel] = useState(null);
  const [loading, setLoading] = useState(() => !getCached("manuscripts:all"));
  const [tab, setTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [view, setView] = useState("manuscripts");
  const [journalDate, setJournalDate] = useState(() => localStorage.getItem("sx_journal_date") || "");
  const [dateMsg, setDateMsg] = useState("");
  const [editingManuscript, setEditingManuscript] = useState(null);
  const [resolveStatus, setResolveStatus] = useState("");
  const EMPTY_FORM = { title: "", authors: "", affiliations: "", author_email: "", category: "", abstract: "", keywords: "", cover_letter: "" };
  const [aForm, setAForm] = useState(EMPTY_FORM);
  const [aFile, setAFile] = useState(null);
  const [aLoading, setALoading] = useState(false);
  const [aDone, setADone] = useState(false);
  const [aErrors, setAErrors] = useState({});

  const refresh = useCallback((showSpinner = false) => {
    if (showSpinner) setLoading(true);
    let isMounted = true;
    fetchAllManuscripts().then((d) => {
      if (isMounted) {
        setData(d || []);
        setLoading(false);
      }
    });
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    const cached = getCached("manuscripts:all");
    if (cached && cached.length > 0) {
      setData(cached);
      setLoading(false);
    }
    refresh(false);
  }, [refresh]);

  const update = async (id, status, extra = {}) => {
    // Optimistic UI update
    setData((prev) => prev.map((m) => m.id === id ? { ...m, status, ...extra } : m));
    if (sel && sel.id === id) {
      setSel((prev) => prev ? { ...prev, status, ...extra } : null);
    }
    await updateManuscriptStatus(id, status, extra);
    refresh(false);
  };

  const counts = useMemo(() => {
    return ADMIN_STATUS_TABS.reduce((acc, t) => {
      acc[t.key] = t.key === "all" ? data.length : data.filter((m) => m.status === t.key).length;
      return acc;
    }, {});
  }, [data]);

  const filtered = useMemo(() => {
    let list = tab === "all" ? data : data.filter((m) => m.status === tab);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter((m) =>
        m.title?.toLowerCase().includes(q) ||
        m.authors?.toLowerCase().includes(q) ||
        m.author_email?.toLowerCase().includes(q) ||
        m.category?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [tab, data, searchQuery]);

  const validateAdmin = (f) => {
    const e = {};
    if (!f.title.trim()) e.title = "Required";
    if (!f.authors.trim()) e.authors = "Required";
    if (!f.affiliations.trim()) e.affiliations = "Required";
    if (!f.author_email.trim()) e.author_email = "Required";
    else if (!/^[^@]+@[^@]+\.[^@]+$/.test(f.author_email)) e.author_email = "Invalid email";
    if (!f.category) e.category = "Required";
    if (!f.abstract.trim()) e.abstract = "Required";
    return e;
  };

  const sendAdmin = async () => {
    const errs = validateAdmin(aForm);
    setAErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setALoading(true);
    try {
      let pdf_url = null;
      if (aFile) {
        pdf_url = await uploadPDF(aFile, aForm.title);
      }
      await insertSubmission({ ...aForm, pdf_url, submitted_at: new Date().toISOString() });
      setADone(true);
      refresh();
    } catch (e) {
      window.alert(e.message);
    } finally {
      setALoading(false);
    }
  };

  const setCustomDate = (dateStr) => {
    if (dateStr) {
      localStorage.setItem("sx_journal_date", dateStr);
      setJournalDate(dateStr);
      setDateMsg(`Date set to ${dateStr}. Refresh to see in header.`);
      window.dispatchEvent(new StorageEvent("storage", { key: "sx_journal_date", newValue: dateStr }));
    } else {
      localStorage.removeItem("sx_journal_date");
      setJournalDate("");
      setDateMsg("Reset to current date.");
      window.dispatchEvent(new StorageEvent("storage", { key: "sx_journal_date", newValue: null }));
    }
    setTimeout(() => setDateMsg(""), 3000);
  };

  // ─── RESOLVE PREVIOUS LINKS ──────────────────────────────────────────────
  const resolvePreviousLinks = async () => {
    if (!window.confirm("This will update all existing pdf_url fields to the new relative format (/papers/...). Continue?")) return;
    setResolveStatus("Resolving...");
    try {
      const manuscripts = await fetchAllManuscripts();
      let updated = 0;
      for (const m of manuscripts) {
        const oldUrl = m.pdf_url;
        if (!oldUrl) continue;
        const filename = extractFilenameFromUrl(oldUrl);
        if (!filename) continue;
        if (oldUrl.startsWith("/papers/")) continue;
        const newUrl = `/papers/${filename}`;
        await updateManuscript(m.id, { pdf_url: newUrl });
        updated++;
      }
      setResolveStatus(`✅ Resolved ${updated} links.`);
      refresh();
    } catch (e) {
      setResolveStatus(`❌ Error: ${e.message}`);
    }
  };

  const AF = ({ name, label, required, children }) => (
    <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
      <label style={{ fontFamily: "var(--mono)", fontSize: "9px", color: "#999", letterSpacing: "0.1em", textTransform: "uppercase" }}>
        {label}{required && <span style={{ color: "var(--scarlet)", marginLeft: "3px" }}>*</span>}
      </label>
      {children}
      {aErrors[name] && <span style={{ fontFamily: "var(--mono)", fontSize: "9px", color: "var(--scarlet)" }}>{aErrors[name]}</span>}
    </div>
  );
  const aFieldStyle = (name) => ({ padding: "10px 12px", border: `1px solid ${aErrors[name] ? "var(--scarlet)" : "#DDD"}`, fontFamily: "var(--serif-body)", fontSize: "14px", outline: "none", width: "100%" });

  return (
    <div style={{ background: "#F6F6F4", minHeight: "100vh" }}>
      <div style={{ background: "var(--ink)", color: "white", height: "56px", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 24px", position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
          <span style={{ fontFamily: "var(--serif-display)", fontSize: "18px", fontWeight: 700 }}>SX Admin</span>
          <span style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "rgba(255,255,255,0.3)", letterSpacing: "0.08em" }}>EDITORIAL PORTAL</span>
        </div>
        <div style={{ display: "flex", gap: "12px" }}>
          <button onClick={() => navigate("home")} style={{ background: "none", border: "1px solid rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.7)", padding: "5px 14px", cursor: "pointer", fontFamily: "var(--mono)", fontSize: "10px", letterSpacing: "0.08em", textTransform: "uppercase" }}>View Site</button>
          <button onClick={onLogout} style={{ background: "var(--scarlet)", border: "none", color: "white", padding: "5px 14px", cursor: "pointer", fontFamily: "var(--mono)", fontSize: "10px", letterSpacing: "0.08em", textTransform: "uppercase" }}>Logout</button>
        </div>
      </div>
      <div style={{ background: "white", borderBottom: "1px solid #DDD", padding: "0 24px", display: "flex", gap: "0", overflowX: "auto" }}>
        {[
          { key: "manuscripts", label: "Manuscripts" },
          { key: "scholar", label: "🎓 Google Scholar & Authors" },
          { key: "submit", label: "+ Submit Manuscript" },
          { key: "formatter", label: "⚡ Word to Template Formatter" },
          { key: "settings", label: "⚙ Journal Settings" },
        ].map((v) => (
          <button
            key={v.key}
            onClick={() => {
              if (v.key === "formatter") {
                navigate("formatter");
                return;
              }
              setView(v.key);
              if (v.key === "manuscripts") setSel(null);
            }}
            style={{
              padding: "16px 24px",
              background: "none",
              border: "none",
              cursor: "pointer",
              borderBottom: `2px solid ${view === v.key ? "var(--scarlet)" : "transparent"}`,
              fontFamily: "var(--mono)",
              fontSize: "11px",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: view === v.key ? "var(--scarlet)" : (v.key === "formatter" ? "#B91C1C" : "var(--ink-70)"),
              fontWeight: v.key === "formatter" ? 700 : 400,
              whiteSpace: "nowrap",
            }}
          >
            {v.label}
          </button>
        ))}
      </div>

      {view === "scholar" && (
        <AdminScholarPanel articles={data.filter((m) => m.status === "published")} navigate={navigate} />
      )}

      {view === "settings" && (
        <div style={{ padding: "32px 24px", maxWidth: "600px" }}>
          <div style={{ background: "white", border: "1px solid #DDD", padding: "28px" }}>
            <div style={{ fontFamily: "var(--serif-display)", fontSize: "18px", fontWeight: 700, marginBottom: "8px" }}>Journal Dateline</div>
            <p style={{ fontSize: "14px", color: "#555", marginBottom: "24px" }}>Override the date displayed in the journal header. Leave empty to use the current system date.</p>
            <div style={{ display: "flex", gap: "16px", alignItems: "flex-end", flexWrap: "wrap" }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "#999", letterSpacing: "0.08em", textTransform: "uppercase", display: "block", marginBottom: "6px" }}>Custom Date</label>
                <input type="date" value={journalDate} onChange={(e) => setJournalDate(e.target.value)} style={{ padding: "10px 12px", border: "1px solid #DDD", fontFamily: "var(--serif-body)", fontSize: "14px", width: "100%" }} />
              </div>
              <button onClick={() => setCustomDate(journalDate)} className="btn btn-primary btn-sm" style={{ padding: "10px 18px" }}>Apply Date</button>
              <button onClick={() => setCustomDate("")} className="btn btn-ghost btn-sm" style={{ padding: "10px 18px" }}>Reset to Today</button>
            </div>
            {dateMsg && <div style={{ marginTop: "12px", fontFamily: "var(--mono)", fontSize: "11px", color: "#16A34A" }}>{dateMsg}</div>}
            <div className="rule" style={{ margin: "24px 0" }} />
            <div style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "#999", letterSpacing: "0.05em" }}>Current header date: {journalDate || "today's date"}</div>
          </div>

          {/* Resolve Previous Links Button */}
          <div style={{ background: "white", border: "1px solid #DDD", padding: "28px", marginTop: "20px" }}>
            <div style={{ fontFamily: "var(--serif-display)", fontSize: "18px", fontWeight: 700, marginBottom: "8px" }}>Fix PDF Links</div>
            <p style={{ fontSize: "14px", color: "#555", marginBottom: "20px" }}>
              This will convert all existing <code>pdf_url</code> fields from absolute Supabase URLs to the new relative format <code>/papers/filename.pdf</code>.
              Run this once after deploying the new version.
            </p>
            <button onClick={resolvePreviousLinks} className="btn btn-primary" style={{ marginRight: "12px" }} disabled={resolveStatus === "Resolving..."}>
              {resolveStatus === "Resolving..." ? "Working…" : "Resolve Previous Links"}
            </button>
            {resolveStatus && <span style={{ fontFamily: "var(--mono)", fontSize: "12px", color: resolveStatus.startsWith("✅") ? "#16A34A" : "#DC2626" }}>{resolveStatus}</span>}
          </div>
        </div>
      )}

      {view === "submit" && (
        <div style={{ padding: "32px 24px", maxWidth: "740px" }}>
          {aDone ? (
            <div style={{ background: "white", border: "1px solid #DDD", padding: "48px", textAlign: "center" }}>
              <div style={{ fontSize: "40px", marginBottom: "12px" }}>✅</div>
              <div style={{ fontFamily: "var(--serif-display)", fontSize: "20px", fontWeight: 700, marginBottom: "8px" }}>Manuscript Added to Queue</div>
              <p style={{ color: "#777", fontSize: "14px", marginBottom: "20px" }}>It has been added to the pending submissions list.</p>
              <button onClick={() => { setADone(false); setAForm(EMPTY_FORM); setAFile(null); setAErrors({}); }} style={{ padding: "10px 24px", background: "var(--ink)", color: "white", border: "none", cursor: "pointer", fontFamily: "var(--mono)", fontSize: "11px", letterSpacing: "0.08em", textTransform: "uppercase", marginRight: "10px" }}>Submit Another</button>
              <button onClick={() => setView("manuscripts")} style={{ padding: "10px 24px", background: "none", color: "var(--ink)", border: "1px solid #DDD", cursor: "pointer", fontFamily: "var(--mono)", fontSize: "11px", letterSpacing: "0.08em", textTransform: "uppercase" }}>View Manuscripts</button>
            </div>
          ) : (
            <div style={{ background: "white", border: "1px solid #DDD", padding: "28px" }}>
              <div style={{ fontFamily: "var(--serif-display)", fontSize: "18px", fontWeight: 700, marginBottom: "4px" }}>Submit a Manuscript</div>
              <p style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "#999", letterSpacing: "0.05em", marginBottom: "24px" }}>Direct admin submission — added immediately to the pending queue.</p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "18px" }}>
                <AF name="title" label="Manuscript Title" required>
                  <input style={aFieldStyle("title")} placeholder="Full title" value={aForm.title} onChange={(e) => setAForm((f) => ({ ...f, title: e.target.value }))} />
                </AF>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                  <AF name="authors" label="All Authors" required>
                    <input style={aFieldStyle("authors")} placeholder="Last, First; Last, First" value={aForm.authors} onChange={(e) => setAForm((f) => ({ ...f, authors: e.target.value }))} />
                  </AF>
                  <AF name="affiliations" label="Author Affiliations" required>
                    <input style={aFieldStyle("affiliations")} placeholder="University of Example; Another Institution" value={aForm.affiliations} onChange={(e) => setAForm((f) => ({ ...f, affiliations: e.target.value }))} />
                  </AF>
                </div>
                <AF name="author_email" label="Corresponding Email" required>
                  <input style={aFieldStyle("author_email")} type="email" placeholder="author@institution.edu" value={aForm.author_email} onChange={(e) => setAForm((f) => ({ ...f, author_email: e.target.value }))} />
                </AF>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                  <AF name="category" label="Subject Category" required>
                    <select style={aFieldStyle("category")} value={aForm.category} onChange={(e) => setAForm((f) => ({ ...f, category: e.target.value }))}>
                      <option value="">Select…</option>
                      {CATEGORY_LIST.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </AF>
                  <AF name="keywords" label="Keywords">
                    <input style={aFieldStyle("keywords")} placeholder="comma-separated" value={aForm.keywords} onChange={(e) => setAForm((f) => ({ ...f, keywords: e.target.value }))} />
                  </AF>
                </div>
                <AF name="abstract" label="Abstract" required>
                  <textarea rows="5" style={{ ...aFieldStyle("abstract"), resize: "vertical" }} placeholder="Structured abstract…" value={aForm.abstract} onChange={(e) => setAForm((f) => ({ ...f, abstract: e.target.value }))} />
                </AF>
                <AF name="file" label="Manuscript File (PDF or DOCX · max 25 MB)">
                  <Dropzone file={aFile} onFile={setAFile} error={false} />
                </AF>
                <AF name="cover_letter" label="Cover Letter">
                  <textarea rows="3" style={{ ...aFieldStyle("cover_letter"), resize: "vertical" }} placeholder="Optional cover letter…" value={aForm.cover_letter} onChange={(e) => setAForm((f) => ({ ...f, cover_letter: e.target.value }))} />
                </AF>
                <button onClick={sendAdmin} disabled={aLoading} style={{ padding: "13px", background: "var(--ink)", color: "white", border: "none", cursor: aLoading ? "not-allowed" : "pointer", fontFamily: "var(--mono)", fontSize: "11px", letterSpacing: "0.1em", textTransform: "uppercase", opacity: aLoading ? 0.6 : 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}>
                  {aLoading ? <><span style={{ width: "12px", height: "12px", border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "white", borderRadius: "50%", animation: "spinDot 0.7s linear infinite", display: "block" }} />Submitting…</> : "Submit Manuscript to Queue"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {view === "manuscripts" && (
        <>
          <div style={{ background: "white", borderBottom: "1px solid #DDD", padding: "0 24px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
            <div style={{ display: "flex", gap: "0", overflowX: "auto" }}>
              {ADMIN_STATUS_TABS.map((t) => (
                <button key={t.key} onClick={() => setTab(t.key)} style={{ padding: "13px 20px", background: "none", border: "none", cursor: "pointer", borderBottom: `2px solid ${tab === t.key ? "var(--scarlet)" : "transparent"}`, fontFamily: "var(--mono)", fontSize: "10px", letterSpacing: "0.08em", textTransform: "uppercase", color: tab === t.key ? "var(--scarlet)" : "var(--ink-70)", display: "flex", alignItems: "center", gap: "7px", whiteSpace: "nowrap" }}>
                  {t.label}
                  <span style={{ background: tab === t.key ? "var(--scarlet)" : "#EEE", color: tab === t.key ? "white" : "var(--ink-70)", padding: "1px 7px", borderRadius: "100px", fontSize: "10px" }}>{counts[t.key]}</span>
                </button>
              ))}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 0" }}>
              <input
                type="text"
                placeholder="Filter submissions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ padding: "6px 12px", border: "1px solid #DDD", fontFamily: "var(--mono)", fontSize: "11px", outline: "none", width: "190px" }}
              />
              <button
                onClick={() => refresh(true)}
                title="Refresh submissions"
                style={{ padding: "6px 10px", background: "none", border: "1px solid #DDD", cursor: "pointer", fontFamily: "var(--mono)", fontSize: "10px", color: "var(--ink-70)", display: "flex", alignItems: "center", gap: "4px" }}
              >
                ↻ Refresh
              </button>
            </div>
          </div>
          <div style={{ padding: "24px", display: "grid", gridTemplateColumns: sel ? "1fr 420px" : "1fr", gap: "20px", alignItems: "start" }}>
            <div style={{ background: "white", border: "1px solid #DDD" }}>
              {loading ? (
                Array(4).fill(0).map((_, i) => (
                  <div key={i} style={{ padding: "16px 20px", borderBottom: "1px solid #EEE" }}>
                    <div className="skeleton" style={{ width: "80px", height: "16px", marginBottom: "8px" }} />
                    <div className="skeleton" style={{ width: "65%", height: "16px" }} />
                  </div>
                ))
              ) : filtered.length === 0 ? (
                <div style={{ padding: "60px", textAlign: "center", color: "#999", fontStyle: "italic" }}>No submissions in this category.</div>
              ) : (
                filtered.map((m) => {
                  const meta = STATUS_META[m.status] || STATUS_META.pending;
                  return (
                    <div key={m.id} onClick={() => setSel(m)} style={{ padding: "16px 20px", borderBottom: "1px solid #EEE", cursor: "pointer", background: sel?.id === m.id ? "#FFF8F7" : "white", borderLeft: sel?.id === m.id ? "3px solid var(--scarlet)" : "3px solid transparent", transition: "background 0.15s, border-color 0.15s" }}>
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px" }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "6px", flexWrap: "wrap" }}>
                            <span style={{ background: meta.bg, color: meta.fg, fontFamily: "var(--mono)", fontSize: "9px", fontWeight: 600, padding: "2px 8px", letterSpacing: "0.08em", textTransform: "uppercase", borderRadius: "2px" }}>{meta.label}</span>
                            {m.category && <span style={{ fontFamily: "var(--mono)", fontSize: "9px", color: "#999", letterSpacing: "0.05em" }}>{m.category}</span>}
                            {m.volume && <span style={{ fontFamily: "var(--mono)", fontSize: "9px", color: "#3730A3", background: "#EEF2FF", padding: "1px 6px", borderRadius: "2px" }}>Vol.{m.volume}{m.issue ? ` No.${m.issue}` : ""}{m.pub_year ? ` · ${m.pub_year}` : ""}</span>}
                          </div>
                          <div style={{ fontFamily: "var(--serif-display)", fontSize: "15px", fontWeight: 600, color: "var(--ink)", lineHeight: 1.3 }}>{m.title}</div>
                          {m.author_email && <div style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "#999", marginTop: "4px" }}>{m.author_email}</div>}
                        </div>
                        <svg style={{ flexShrink: 0, color: "#CCC", marginTop: "4px" }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9,18 15,12 9,6" /></svg>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            {sel && (
              <div style={{ background: "white", border: "1px solid #DDD", position: "sticky", top: "76px" }}>
                <div style={{ padding: "20px 20px 16px", borderBottom: "1px solid #EEE", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "#999", letterSpacing: "0.08em", textTransform: "uppercase" }}>Submission Detail</span>
                  <button onClick={() => setSel(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "#999", fontSize: "18px", lineHeight: 1 }}>×</button>
                </div>
                <div style={{ padding: "20px" }}>
                  {[
                    { label: "Title", value: sel.title },
                    { label: "Authors", value: sel.authors },
                    { label: "Affiliations", value: sel.affiliations },
                    { label: "Email", value: sel.author_email },
                    { label: "Category", value: sel.category },
                    { label: "Keywords", value: sel.keywords },
                    { label: "Status", value: STATUS_META[sel.status]?.label || sel.status },
                    { label: "Volume", value: sel.volume ? `Vol. ${sel.volume}` : null },
                    { label: "Issue", value: sel.issue ? `No. ${sel.issue}` : null },
                    { label: "DOI Link", value: getManuscriptDoi(sel) ? (
                      <a href={getManuscriptDoi(sel)} target="_blank" rel="noreferrer" style={{ color: "var(--scarlet)", textDecoration: "underline", wordBreak: "break-all" }}>
                        {getManuscriptDoi(sel)} ↗
                      </a>
                    ) : null },
                    { label: "Published Date", value: sel.published_at ? new Date(sel.published_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : null },
                    { label: "Submitted", value: sel.submitted_at ? new Date(sel.submitted_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "—" },
                  ].map(({ label, value }) => value && (
                    <div key={label} style={{ marginBottom: "14px" }}>
                      <div style={{ fontFamily: "var(--mono)", fontSize: "9px", color: "#999", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "3px" }}>{label}</div>
                      <div style={{ fontSize: "14px", fontFamily: "var(--serif-display)", fontWeight: 600, color: "var(--ink)" }}>{value}</div>
                    </div>
                  ))}
                  {sel.abstract && (
                    <div style={{ marginBottom: "20px" }}>
                      <div style={{ fontFamily: "var(--mono)", fontSize: "9px", color: "#999", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "6px" }}>Abstract</div>
                      <p style={{ fontSize: "13px", lineHeight: "1.7", color: "#555" }}>{sel.abstract}</p>
                    </div>
                  )}
                  {sel.cover_letter && (
                    <div style={{ marginBottom: "20px" }}>
                      <div style={{ fontFamily: "var(--mono)", fontSize: "9px", color: "#999", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "6px" }}>Cover Letter</div>
                      <p style={{ fontSize: "13px", lineHeight: "1.7", color: "#555" }}>{sel.cover_letter}</p>
                    </div>
                  )}
                  {sel.pdf_url && (
                    <div style={{ marginBottom: "20px" }}>
                      <div style={{ fontFamily: "var(--mono)", fontSize: "9px", color: "#999", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "8px" }}>Manuscript File</div>
                      <a href={sel.pdf_url} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: "7px", padding: "9px 16px", background: "#F0F9FF", color: "#0C4A6E", border: "1px solid #7DD3FC", fontFamily: "var(--mono)", fontSize: "10px", letterSpacing: "0.08em", textTransform: "uppercase", textDecoration: "none" }}>
                        📄 View / Download
                      </a>
                    </div>
                  )}
                  <div style={{ borderTop: "1px solid #EEE", paddingTop: "16px", display: "flex", flexDirection: "column", gap: "8px" }}>
                    <button
                      onClick={() => {
                        try {
                          localStorage.setItem("sx_formatter_draft", JSON.stringify({
                            title: (sel.title || "").toUpperCase(),
                            authors: sel.authors || "",
                            affiliations: sel.affiliations || "",
                            correspondenceEmail: sel.author_email || "",
                            correspondenceName: sel.authors?.split(";")?.[0]?.replace(/[0-9,*]/g, "").trim() || "",
                            articleType: sel.category || "Review Article",
                            abstract: sel.abstract || "",
                            keywords: sel.keywords || "",
                            volume: sel.volume || "8",
                            issue: sel.issue || "2",
                            year: sel.pub_year || (sel.published_at ? new Date(sel.published_at).getFullYear() : 2026),
                            doi: getManuscriptDoi(sel) || "",
                            publishedDate: sel.published_at ? sel.published_at.slice(0, 10) : "2026",
                          }));
                        } catch (e) {}
                        navigate("formatter");
                      }}
                      style={{ padding: "11px", background: "#FEF3C7", color: "#92400E", border: "1px solid #FCD34D", cursor: "pointer", fontFamily: "var(--mono)", fontSize: "11px", letterSpacing: "0.08em", textTransform: "uppercase" }}
                    >
                      📄 Format in Journal Template
                    </button>
                    <button onClick={() => setEditingManuscript(sel)} style={{ padding: "11px", background: "#E0E7FF", color: "#1E3A8A", border: "1px solid #A5B4FC", cursor: "pointer", fontFamily: "var(--mono)", fontSize: "11px", letterSpacing: "0.08em", textTransform: "uppercase" }}>✎ Edit Manuscript</button>
                    <PublishForm manuscript={sel} onPublish={async (extra) => { await update(sel.id, "published", extra); }} />
                    {sel.status !== "revision" && <button onClick={() => update(sel.id, "revision")} style={{ padding: "11px", background: "#E0F2FE", color: "#0C4A6E", border: "1px solid #7DD3FC", cursor: "pointer", fontFamily: "var(--mono)", fontSize: "11px", letterSpacing: "0.08em", textTransform: "uppercase" }}>↻ Request Revision</button>}
                    {sel.status !== "rejected" && <button onClick={() => update(sel.id, "rejected")} style={{ padding: "11px", background: "#FEE2E2", color: "#7F1D1D", border: "1px solid #FCA5A5", cursor: "pointer", fontFamily: "var(--mono)", fontSize: "11px", letterSpacing: "0.08em", textTransform: "uppercase" }}>✕ Reject</button>}
                    <button onClick={() => deleteManuscript(sel.id).then(refresh).then(() => setSel(null))} style={{ padding: "11px", background: "white", color: "#DC2626", border: "1.5px solid #DC2626", cursor: "pointer", fontFamily: "var(--mono)", fontSize: "11px", letterSpacing: "0.08em", textTransform: "uppercase", marginTop: "4px" }}>⚠ Delete Permanently</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
      {editingManuscript && (
        <EditManuscriptModal manuscript={editingManuscript} onClose={() => setEditingManuscript(null)} onSaved={() => { refresh(); setSel(null); }} />
      )}
    </div>
  );
}

function AdminPageWrapper({ navigate }) {
  const [auth, setAuth] = useState(() => sessionStorage.getItem("sx_admin") === "1");
  const [pass, setPass] = useState("");
  const [shake, setShake] = useState(false);
  const tryLogin = () => {
    if (pass === ADMIN_PASS) {
      sessionStorage.setItem("sx_admin", "1");
      setAuth(true);
    } else {
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setPass("");
    }
  };
  if (!auth)
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--ink)" }}>
        <style>{`@keyframes shake { 0%,100%{transform:translateX(0)} 20%,60%{transform:translateX(-8px)} 40%,80%{transform:translateX(8px)} }`}</style>
        <div className="animate-up" style={{ width: "360px", padding: "0 24px" }}>
          <div style={{ fontFamily: "var(--serif-display)", fontSize: "26px", fontWeight: 700, color: "white", marginBottom: "6px" }}>Editorial Admin</div>
          <div style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "rgba(255,255,255,0.3)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "32px" }}>ScienceXplorer Portal</div>
          <div style={{ animation: shake ? "shake 0.4s ease" : "none" }}>
            <label style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "rgba(255,255,255,0.4)", letterSpacing: "0.1em", textTransform: "uppercase", display: "block", marginBottom: "8px" }}>Access Key</label>
            <input type="password" value={pass} onChange={(e) => setPass(e.target.value)} onKeyDown={(e) => e.key === "Enter" && tryLogin()} style={{ width: "100%", padding: "14px 16px", background: "rgba(255,255,255,0.06)", border: "1.5px solid rgba(255,255,255,0.15)", color: "white", fontFamily: "var(--serif-body)", fontSize: "16px", outline: "none", marginBottom: "12px" }} placeholder="Enter access key" autoFocus />
            <button onClick={tryLogin} style={{ width: "100%", padding: "14px", background: "var(--scarlet)", border: "none", color: "white", cursor: "pointer", fontFamily: "var(--mono)", fontSize: "11px", letterSpacing: "0.1em", textTransform: "uppercase" }}>Enter Portal</button>
          </div>
        </div>
      </div>
    );
  return <AdminDashboard navigate={navigate} onLogout={() => { sessionStorage.removeItem("sx_admin"); setAuth(false); }} />;
}

/* ─────────────────────────────────────────────────────────────────────────
   ADMIN FORMATTER GATE (ADMIN ONLY ACCESS)
   ───────────────────────────────────────────────────────────────────────── */
function AdminFormatterWrapper({ navigate }) {
  const [auth, setAuth] = useState(() => sessionStorage.getItem("sx_admin") === "1");
  const [pass, setPass] = useState("");
  const [shake, setShake] = useState(false);

  const tryLogin = () => {
    if (pass === ADMIN_PASS) {
      sessionStorage.setItem("sx_admin", "1");
      setAuth(true);
    } else {
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setPass("");
    }
  };

  if (!auth) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--ink)" }}>
        <style>{`@keyframes shake { 0%,100%{transform:translateX(0)} 20%,60%{transform:translateX(-8px)} 40%,80%{transform:translateX(8px)} }`}</style>
        <div className="animate-up" style={{ width: "380px", padding: "0 24px" }}>
          <div style={{ display: "inline-block", background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B", fontFamily: "var(--mono)", fontSize: "10px", fontWeight: 700, padding: "3px 8px", borderRadius: "3px", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "12px" }}>
            Admin Authorization Required
          </div>
          <div style={{ fontFamily: "var(--serif-display)", fontSize: "24px", fontWeight: 700, color: "white", marginBottom: "6px" }}>
            Word-to-Template Formatter
          </div>
          <p style={{ fontFamily: "var(--serif-body)", fontSize: "14px", color: "rgba(255,255,255,0.6)", lineHeight: 1.6, marginBottom: "24px" }}>
            The automatic Word document layout formatter is an editorial production tool restricted strictly to ScienceXplorer editors.
          </p>
          <div style={{ animation: shake ? "shake 0.4s ease" : "none" }}>
            <label style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "rgba(255,255,255,0.4)", letterSpacing: "0.1em", textTransform: "uppercase", display: "block", marginBottom: "8px" }}>
              Admin Access Key
            </label>
            <input
              type="password"
              value={pass}
              onChange={(e) => setPass(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && tryLogin()}
              style={{ width: "100%", padding: "14px 16px", background: "rgba(255,255,255,0.06)", border: "1.5px solid rgba(255,255,255,0.15)", color: "white", fontFamily: "var(--serif-body)", fontSize: "16px", outline: "none", marginBottom: "14px", boxSizing: "border-box" }}
              placeholder="Enter admin password"
              autoFocus
            />
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={tryLogin}
                style={{ flex: 1, padding: "13px", background: "var(--scarlet)", border: "none", color: "white", cursor: "pointer", fontFamily: "var(--mono)", fontSize: "11px", letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 700 }}
              >
                Access Formatter
              </button>
              <button
                onClick={() => navigate("home")}
                style={{ padding: "13px 18px", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", color: "white", cursor: "pointer", fontFamily: "var(--mono)", fontSize: "11px" }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <ManuscriptFormatter onBack={() => navigate("admin")} />;
}

/* ─────────────────────────────────────────────────────────────────────────
   ABOUT PAGE
   ───────────────────────────────────────────────────────────────────────── */
function AboutPage({ navigate }) {
  const commitments = [
    { title: "Double-Blind Peer Review", text: "Every submitted manuscript is anonymized and reviewed independently by qualified subject experts to ensure impartial evaluation based strictly on scholarly merit." },
    { title: "Full Open Access Under CC-BY 4.0", text: "Authors retain full copyright of their research. All articles are permanently and freely accessible online for researchers, practitioners, and the public." },
    { title: "Transparent & Equitable Publishing", text: "To sustain independent operations without institutional paywalls, APC is evaluated on a case-by-case basis upon acceptance, with full waivers and subsidies available for eligible researchers." },
    { title: "Methodological Integrity", text: "We prioritize research validity, clear methodologies, and sound analysis over novelty or commercial sensationalism, welcoming replications and negative results." },
  ];
  const values = [
    { icon: "◈", title: "Open Access", body: "Every article published in ScienceXplorer is immediately and permanently free to read, download, and share under CC-BY 4.0. We believe scientific knowledge should be accessible to all." },
    { icon: "◎", title: "Rigorous Peer Review", body: "Our double-blind review process pairs each submission with independent subject-matter experts, focusing on research integrity, experimental design, and reproducibility." },
    { icon: "◇", title: "Interdisciplinary Scope", body: "We actively encourage research that bridges academic boundaries, publishing cross-disciplinary investigations across science, medicine, technology, and social sciences." },
    { icon: "○", title: "Author-First Policy", body: "Authors retain complete ownership of their work. We offer transparent communication throughout the review cycle and ensure prompt editorial desk assessments." },
  ];
  return (
    <div>
      <div style={{ background: "var(--ink)", padding: "80px 0 72px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle at 30% 60%, rgba(192,57,43,0.12) 0%, transparent 55%)", pointerEvents: "none" }} />
        <div className="container" style={{ position: "relative", maxWidth: "760px" }}>
          <div className="label animate-up" style={{ color: "rgba(255,255,255,0.4)", marginBottom: "16px" }}>Open Access Research</div>
          <h1 className="display-xl animate-up animate-up-d1" style={{ color: "white", marginBottom: "24px" }}>About<br /><span style={{ color: "var(--scarlet)", fontStyle: "italic" }}>ScienceXplorer</span></h1>
          <p className="animate-up animate-up-d2" style={{ fontSize: "18px", color: "rgba(255,255,255,0.6)", lineHeight: 1.8, maxWidth: "580px" }}>An independent, peer-reviewed open-access journal committed to publishing rigorous scholarly research across academic disciplines.</p>
        </div>
      </div>
      <div style={{ background: "white", borderBottom: "1px solid var(--ink-15)" }}>
        <div className="container" style={{ padding: "72px 24px", maxWidth: "900px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "64px", alignItems: "start" }}>
            <div className="animate-up">
              <div className="label" style={{ color: "var(--ink-40)", marginBottom: "14px" }}>Our Mission</div>
              <div className="rule-heavy" style={{ marginBottom: "24px" }} />
              <p style={{ fontSize: "17px", lineHeight: "1.85", color: "var(--ink-70)", marginBottom: "18px" }}>ScienceXplorer is dedicated to removing barriers to academic dissemination. We provide a rigorous, transparent, and prompt peer-reviewed venue for scholars and scientists worldwide.</p>
              <p style={{ fontSize: "17px", lineHeight: "1.85", color: "var(--ink-70)" }}>We publish original empirical studies, systematic reviews, and theoretical contributions across eight primary academic fields, ensuring research is freely accessible without reader paywalls.</p>
            </div>
            <div className="animate-up animate-up-d1">
              <div className="label" style={{ color: "var(--ink-40)", marginBottom: "14px" }}>Publishing Standards</div>
              <div className="rule-heavy" style={{ marginBottom: "24px" }} />
              {[
                { n: "Open Access", l: "Publishing Model" },
                { n: "Double-Blind", l: "Peer Review Protocol" },
                { n: "CC-BY 4.0", l: "Author Copyright License" },
                { n: "Case-by-Case", l: "APC Model (Waivers Available)" },
                { n: "8 Fields", l: "Disciplines Covered" },
                { n: "Continuous", l: "Publication Frequency" },
              ].map(({ n, l }) => (
                <div key={l} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "11px 0", borderBottom: "1px solid var(--ink-07)" }}>
                  <span style={{ fontFamily: "var(--mono)", fontSize: "10px", letterSpacing: "0.05em", textTransform: "uppercase", color: "var(--ink-40)" }}>{l}</span>
                  <span style={{ fontFamily: "var(--serif-display)", fontSize: "18px", fontWeight: 700 }}>{n}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="container" style={{ padding: "72px 24px" }}>
        <div className="animate-up" style={{ marginBottom: "48px" }}>
          <div className="label" style={{ color: "var(--ink-40)", marginBottom: "12px" }}>What We Stand For</div>
          <div className="rule-heavy" />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "32px" }}>
          {values.map((v, i) => (
            <div key={v.title} className="animate-up" style={{ animationDelay: `${i * 0.08}s` }}>
              <div style={{ fontFamily: "var(--serif-display)", fontSize: "28px", color: "var(--scarlet)", marginBottom: "14px", lineHeight: 1 }}>{v.icon}</div>
              <h3 style={{ fontFamily: "var(--serif-display)", fontSize: "19px", fontWeight: 700, marginBottom: "10px" }}>{v.title}</h3>
              <p style={{ fontSize: "15px", lineHeight: "1.8", color: "var(--ink-70)" }}>{v.body}</p>
            </div>
          ))}
        </div>
      </div>
      <div style={{ background: "white", borderTop: "1px solid var(--ink-15)", borderBottom: "1px solid var(--ink-15)" }}>
        <div className="container" style={{ padding: "72px 24px" }}>
          <div className="animate-up" style={{ marginBottom: "48px" }}>
            <div className="label" style={{ color: "var(--ink-40)", marginBottom: "12px" }}>Editorial Principles</div>
            <div className="rule-heavy" />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "28px", maxWidth: "900px" }}>
            {commitments.map((c, i) => (
              <div key={c.title} className="animate-up card" style={{ animationDelay: `${i * 0.06}s`, padding: "28px", background: "var(--cream)" }}>
                <div style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "var(--scarlet)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "8px" }}>Commitment 0{i + 1}</div>
                <h3 style={{ fontFamily: "var(--serif-display)", fontSize: "18px", fontWeight: 700, marginBottom: "10px" }}>{c.title}</h3>
                <p style={{ fontSize: "14px", lineHeight: "1.75", color: "var(--ink-70)" }}>{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="container" style={{ padding: "72px 24px" }}>
        <div className="animate-up" style={{ marginBottom: "40px" }}>
          <div className="label" style={{ color: "var(--ink-40)", marginBottom: "12px" }}>Discovery & Metadata Standards</div>
          <div className="rule-heavy" />
        </div>
        <div className="animate-up animate-up-d1" style={{ display: "flex", flexWrap: "wrap", gap: "16px" }}>
          {["Google Scholar Discoverable", "Dublin Core Metadata Tags", "Highwire Press Tagging", "Open Access Repository", "Permanent Digital PDF Storage", "Creative Commons CC-BY 4.0"].map((db) => (
            <div key={db} style={{ padding: "10px 20px", border: "1px solid var(--ink-15)", background: "white", fontFamily: "var(--mono)", fontSize: "11px", letterSpacing: "0.06em", color: "var(--ink-70)" }}>{db}</div>
          ))}
        </div>
      </div>
      <div style={{ background: "var(--ink)", padding: "72px 0" }}>
        <div className="container" style={{ textAlign: "center", maxWidth: "560px" }}>
          <h2 className="display-md animate-up" style={{ color: "white", marginBottom: "16px" }}>Ready to contribute to open science?</h2>
          <p className="animate-up animate-up-d1" style={{ color: "rgba(255,255,255,0.55)", fontSize: "16px", lineHeight: 1.7, marginBottom: "32px" }}>Submit your research and join scholars committed to accessible, rigorous knowledge.</p>
          <div className="animate-up animate-up-d2" style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={() => navigate("submit")} className="btn btn-primary">Submit Manuscript</button>
            <button onClick={() => navigate("editorial")} className="btn" style={{ borderColor: "rgba(255,255,255,0.3)", color: "white" }}>Editorial Governance</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   EDITORIAL BOARD PAGE
   ───────────────────────────────────────────────────────────────────────── */
function EditorialBoardPage({ navigate }) {
  const sections = [
    { title: "Technology & Engineering", desc: "Computer science, artificial intelligence, mechanical & civil engineering, data systems." },
    { title: "Health & Medicine", desc: "Clinical medicine, epidemiology, public health, biomedical sciences, healthcare delivery." },
    { title: "Natural Sciences", desc: "Physics, chemistry, biology, environmental sciences, earth sciences." },
    { title: "Economics & Business", desc: "Macroeconomics, applied economics, finance, management sciences, development economics." },
    { title: "Social Sciences & Psychology", desc: "Sociology, political science, cognitive science, social psychology, anthropology." },
    { title: "Humanities & Philosophy", desc: "Philosophy, history, literature, linguistics, cultural studies." },
    { title: "Education & Pedagogy", desc: "Curriculum development, educational technology, learning analytics, pedagogy." },
    { title: "Law & Public Policy", desc: "International law, comparative legal studies, jurisprudence, public administration." },
  ];

  return (
    <div>
      <div style={{ background: "var(--cream)", borderBottom: "1px solid var(--ink-15)", padding: "60px 0 56px" }}>
        <div className="container">
          <div className="animate-up" style={{ marginBottom: "6px" }}><div className="label" style={{ color: "var(--ink-40)" }}>Governance & Peer Review</div></div>
          <h1 className="display-lg animate-up animate-up-d1" style={{ marginBottom: "14px" }}>Editorial Board & Governance</h1>
          <p className="animate-up animate-up-d2" style={{ color: "var(--ink-70)", fontSize: "17px", maxWidth: "580px", lineHeight: 1.7 }}>ScienceXplorer is governed by academic standards upholding rigorous, independent double-blind peer review.</p>
        </div>
      </div>

      <div style={{ background: "white", borderBottom: "1px solid var(--ink-15)" }}>
        <div className="container" style={{ padding: "60px 24px", maxWidth: "900px" }}>
          <div className="label animate-up" style={{ color: "var(--ink-40)", marginBottom: "12px" }}>Editorial Structure & Standards</div>
          <div className="rule-heavy animate-up" style={{ marginBottom: "36px" }} />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "28px" }}>
            <div className="animate-up card" style={{ padding: "24px" }}>
              <div style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "var(--scarlet)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "8px" }}>Protocol</div>
              <h3 style={{ fontFamily: "var(--serif-display)", fontSize: "18px", fontWeight: 700, marginBottom: "10px" }}>Double-Blind Peer Review</h3>
              <p style={{ fontSize: "14px", lineHeight: "1.75", color: "var(--ink-70)" }}>Reviewer and author identities are kept strictly confidential during review. Manuscripts are evaluated exclusively on methodological soundess, clarity, and contribution.</p>
            </div>
            <div className="animate-up card" style={{ padding: "24px", animationDelay: "0.08s" }}>
              <div style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "var(--scarlet)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "8px" }}>Independence</div>
              <h3 style={{ fontFamily: "var(--serif-display)", fontSize: "18px", fontWeight: 700, marginBottom: "10px" }}>Editorial Integrity</h3>
              <p style={{ fontSize: "14px", lineHeight: "1.75", color: "var(--ink-70)" }}>Editorial decisions are made independently of publication fee considerations. No author is accepted or rejected based on commercial factors or waiver requests.</p>
            </div>
            <div className="animate-up card" style={{ padding: "24px", animationDelay: "0.16s" }}>
              <div style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "var(--scarlet)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "8px" }}>Ethics</div>
              <h3 style={{ fontFamily: "var(--serif-display)", fontSize: "18px", fontWeight: 700, marginBottom: "10px" }}>COPE Guidelines</h3>
              <p style={{ fontSize: "14px", lineHeight: "1.75", color: "var(--ink-70)" }}>Editors and reviewers follow Committee on Publication Ethics (COPE) standards regarding conflict of interest, confidentiality, plagiarism prevention, and corrections.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container" style={{ padding: "60px 24px" }}>
        <div className="label animate-up" style={{ color: "var(--ink-40)", marginBottom: "12px" }}>Academic Sections</div>
        <div className="rule-heavy animate-up" style={{ marginBottom: "36px" }} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "20px" }}>
          {sections.map((s, i) => (
            <div key={s.title} className="animate-up card" style={{ animationDelay: `${i * 0.05}s`, padding: "24px", background: "white" }}>
              <div style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "var(--scarlet)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "6px" }}>Section 0{i + 1}</div>
              <h3 style={{ fontFamily: "var(--serif-display)", fontSize: "17px", fontWeight: 700, marginBottom: "8px" }}>{s.title}</h3>
              <p style={{ fontSize: "14px", lineHeight: "1.7", color: "var(--ink-70)", marginBottom: "16px" }}>{s.desc}</p>
              <div style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "var(--ink-40)", letterSpacing: "0.05em", textTransform: "uppercase" }}>Open Call for Reviewers</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ background: "white", borderTop: "1px solid var(--ink-15)" }}>
        <div className="container" style={{ padding: "60px 24px", maxWidth: "860px" }}>
          <div className="animate-up" style={{ background: "var(--cream)", border: "1px solid var(--ink-15)", padding: "40px" }}>
            <div className="label" style={{ color: "var(--scarlet)", marginBottom: "12px" }}>Call for Editorial Board Members & Reviewers</div>
            <h2 style={{ fontFamily: "var(--serif-display)", fontSize: "24px", fontWeight: 700, marginBottom: "12px" }}>Join the ScienceXplorer Reviewer & Editorial Network</h2>
            <p style={{ fontSize: "15px", lineHeight: "1.8", color: "var(--ink-70)", marginBottom: "20px" }}>
              ScienceXplorer is expanding its international reviewer roster and editorial committees across all eight disciplines. We invite qualified scholars who hold a doctoral degree (Ph.D., MD, or terminal equivalent) and have an established record of peer-reviewed scholarship to participate in our double-blind review process.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px", marginBottom: "28px" }}>
              <div style={{ background: "white", padding: "16px 20px", border: "1px solid var(--ink-15)" }}>
                <strong style={{ fontFamily: "var(--serif-display)", fontSize: "15px", display: "block", marginBottom: "6px" }}>Section Editors</strong>
                <p style={{ fontSize: "13px", color: "var(--ink-70)", lineHeight: 1.6 }}>Manage manuscript submissions within specialized domains, assign expert reviewers, and recommend editorial decisions.</p>
              </div>
              <div style={{ background: "white", padding: "16px 20px", border: "1px solid var(--ink-15)" }}>
                <strong style={{ fontFamily: "var(--serif-display)", fontSize: "15px", display: "block", marginBottom: "6px" }}>Peer Reviewers</strong>
                <p style={{ fontSize: "13px", color: "var(--ink-70)", lineHeight: 1.6 }}>Provide critical, constructive, and timely evaluation of manuscript methodology, findings, and literature grounding.</p>
              </div>
            </div>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
              <button onClick={() => navigate("submit")} className="btn btn-primary">Apply to Editorial Board</button>
              <button onClick={() => navigate("guidelines")} className="btn btn-ghost">Review Guidelines →</button>
            </div>
          </div>
          <div className="animate-up" style={{ marginTop: "24px", padding: "16px 20px", background: "var(--ink-07)", border: "1px solid var(--ink-15)", fontSize: "13px", color: "var(--ink-70)", lineHeight: 1.7 }}>
            <strong>Transparency Notice:</strong> In adherence to our double-blind peer review policy, reviewer rosters for active evaluation cycles remain confidential. Formal editorial board appointments are published upon completion of accreditation.
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   AUTHOR GUIDELINES PAGE (unchanged)
   ───────────────────────────────────────────────────────────────────────── */
function GuidelinesPage({ navigate }) {
  const [openSection, setOpenSection] = useState(null);
  const toggle = (i) => setOpenSection((o) => (o === i ? null : i));
  const sections = [
    {
      title: "Scope & Submission Types", icon: "01",
      content: (
        <div>
          <p style={{ marginBottom: "16px" }}>ScienceXplorer welcomes original research across all academic disciplines. We accept the following manuscript types:</p>
          <div style={{ display: "grid", gap: "12px" }}>
            {[
              { type: "Original Research Article", desc: "Full empirical studies presenting new data. 4,000–12,000 words. Must include abstract, introduction, methods, results, discussion, and conclusion.", limit: "12,000 words max" },
              { type: "Systematic Review / Meta-Analysis", desc: "Comprehensive synthesis of existing literature following PRISMA or MOOSE guidelines.", limit: "10,000 words max" },
              { type: "Theoretical / Conceptual Paper", desc: "Manuscripts advancing new theoretical frameworks or conceptual models without primary data collection.", limit: "8,000 words max" },
              { type: "Brief Communication", desc: "Short reports of significant preliminary findings or methodological innovations.", limit: "3,000 words max" },
              { type: "Commentary / Perspective", desc: "Expert opinion pieces on current issues, recent publications, or emerging debates in a field.", limit: "2,000 words max" },
            ].map(({ type, desc, limit }) => (
              <div key={type} style={{ background: "var(--cream)", padding: "16px 20px", borderLeft: "3px solid var(--scarlet)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px", flexWrap: "wrap", marginBottom: "6px" }}>
                  <strong style={{ fontFamily: "var(--serif-display)", fontSize: "15px" }}>{type}</strong>
                  <span style={{ fontFamily: "var(--mono)", fontSize: "9px", color: "var(--scarlet)", letterSpacing: "0.08em", textTransform: "uppercase", whiteSpace: "nowrap" }}>{limit}</span>
                </div>
                <p style={{ fontSize: "14px", color: "var(--ink-70)", lineHeight: 1.7 }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      title: "Manuscript Preparation", icon: "02",
      content: (
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div>
            <h4 style={{ fontFamily: "var(--serif-display)", fontSize: "16px", fontWeight: 600, marginBottom: "10px" }}>File Format & Structure</h4>
            <ul style={{ paddingLeft: "20px", lineHeight: "2", fontSize: "15px", color: "var(--ink-70)" }}>
              <li>Submit as PDF or DOCX. LaTeX submissions must include a compiled PDF.</li>
              <li>Use 12pt serif font (Times New Roman or similar), double-spaced, 2.5cm margins.</li>
              <li>Number all pages and lines continuously.</li>
              <li>Do not include author names or affiliations in the manuscript body (double-blind review).</li>
            </ul>
          </div>
          <div>
            <h4 style={{ fontFamily: "var(--serif-display)", fontSize: "16px", fontWeight: 600, marginBottom: "10px" }}>Abstract Requirements</h4>
            <p style={{ fontSize: "15px", lineHeight: "1.8", color: "var(--ink-70)", marginBottom: "10px" }}>Structured abstracts are required for all empirical submissions (max 300 words) using the following subheadings:</p>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {["Background", "Objectives", "Methods", "Results", "Conclusions"].map((h) => (
                <span key={h} style={{ padding: "5px 14px", background: "white", border: "1px solid var(--ink-15)", fontFamily: "var(--mono)", fontSize: "10px", letterSpacing: "0.06em", color: "var(--ink-70)" }}>{h}</span>
              ))}
            </div>
          </div>
          <div>
            <h4 style={{ fontFamily: "var(--serif-display)", fontSize: "16px", fontWeight: 600, marginBottom: "10px" }}>Keywords</h4>
            <p style={{ fontSize: "15px", lineHeight: "1.8", color: "var(--ink-70)" }}>Provide 4–8 keywords that accurately represent your work. Avoid terms already present in the title. Use MeSH terms for biomedical submissions.</p>
          </div>
        </div>
      ),
    },
    {
      title: "Figures, Tables & Data", icon: "03",
      content: (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px", fontSize: "15px", color: "var(--ink-70)", lineHeight: "1.8" }}>
          <p>All figures must be submitted at minimum 300 DPI in TIFF, EPS, or high-resolution PDF format. Figures embedded in Word documents are not acceptable for final production.</p>
          <div style={{ background: "var(--cream)", padding: "16px 20px" }}>
            <strong style={{ fontFamily: "var(--mono)", fontSize: "10px", letterSpacing: "0.08em", textTransform: "uppercase", display: "block", marginBottom: "8px" }}>Figure Caption Format</strong>
            <p style={{ fontStyle: "italic", color: "var(--ink-40)" }}>Figure 1. [Title of figure]. [Brief description including all abbreviations and statistical notations.] n = [sample size]. Error bars represent ± standard deviation. * p &lt; 0.05, ** p &lt; 0.01.</p>
          </div>
          <p>Tables should be editable (not images), submitted in DOCX or within the main manuscript. Each table requires a concise descriptive title above and footnotes below defining all abbreviations.</p>
          <p>Raw data underlying all figures and analyses must be deposited in a publicly accessible repository (Figshare, Zenodo, OSF, Dryad) and the repository URL provided in the manuscript.</p>
        </div>
      ),
    },
    {
      title: "References & Citations", icon: "04",
      content: (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px", fontSize: "15px", color: "var(--ink-70)", lineHeight: "1.8" }}>
          <p>ScienceXplorer uses APA 7th Edition for all disciplines except law submissions (Chicago 17th). References must be complete, accurate, and consistent. We strongly recommend Zotero or Mendeley for reference management.</p>
          <div>
            <strong style={{ fontFamily: "var(--serif-display)", fontSize: "15px", display: "block", marginBottom: "12px" }}>APA 7th Edition Examples</strong>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {[
                { label: "Journal Article", example: "Walton, M. E., & Mars, R. B. (2023). Reconsidering animal models of human decision-making. Nature Reviews Neuroscience, 24(3), 145–162." },
                { label: "Book", example: "Kahneman, D. (2011). Thinking, fast and slow. Farrar, Straus and Giroux." },
                { label: "Chapter in Edited Book", example: "García, A. (2022). Social identity and conflict. In B. Kowalski (Ed.), Handbook of social psychology (3rd ed., pp. 201–238). Sage." },
              ].map(({ label, example }) => (
                <div key={label} style={{ background: "var(--cream)", padding: "14px 18px" }}>
                  <div style={{ fontFamily: "var(--mono)", fontSize: "9px", color: "var(--scarlet)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "6px" }}>{label}</div>
                  <p style={{ fontSize: "13px", fontStyle: "italic", color: "var(--ink-70)", lineHeight: 1.7 }}>{example}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Ethics & Compliance", icon: "05",
      content: (
        <div style={{ display: "flex", flexDirection: "column", gap: "20px", fontSize: "15px", color: "var(--ink-70)", lineHeight: "1.8" }}>
          {[
            { title: "Human Subjects Research", body: "All studies involving human participants must report IRB/Ethics Committee approval (name of committee and reference number). Informed consent procedures must be described in the Methods section." },
            { title: "Animal Research", body: "Studies must comply with ARRIVE 2.0 guidelines. IACUC approval details are mandatory. Species, sex, age, and housing conditions must be fully described." },
            { title: "Authorship", body: "All authors must meet all four ICMJE criteria for authorship. Contributions must be declared using CRediT taxonomy. Changes to authorship after submission require written justification from all authors." },
            { title: "Competing Interests", body: "All authors must disclose financial or non-financial interests that could have influenced the work. Declarations must cover the 36 months prior to submission." },
          ].map(({ title, body }) => (
            <div key={title}>
              <h4 style={{ fontFamily: "var(--serif-display)", fontSize: "16px", fontWeight: 600, color: "var(--ink)", marginBottom: "8px" }}>{title}</h4>
              <p>{body}</p>
            </div>
          ))}
          <div style={{ background: "#FEF9C3", border: "1px solid #FDE68A", padding: "16px 20px" }}>
            <div className="label" style={{ color: "#92400E", marginBottom: "6px" }}>Plagiarism Policy</div>
            <p style={{ fontSize: "14px", color: "#78350F" }}>All submissions are screened with iThenticate. A similarity index above 20% (excluding references) will result in automatic rejection without review. Self-plagiarism applies equally.</p>
          </div>
        </div>
      ),
    },
    {
      title: "Peer Review Process", icon: "06",
      content: (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "1px", background: "var(--ink-07)", marginBottom: "24px" }}>
            {[
              { step: "1", label: "Submission", time: "Day 0", desc: "Manuscript received; desk check for scope and formatting." },
              { step: "2", label: "Desk Review", time: "24–48 hrs", desc: "Editor assesses suitability. ~30% of submissions are desk-rejected." },
              { step: "3", label: "Peer Review", time: "2–4 weeks", desc: "Two independent experts review using double-blind protocol." },
              { step: "4", label: "Decision", time: "Within 72 hrs of reports", desc: "Editor issues Accept, Minor Revision, Major Revision, or Reject." },
              { step: "5", label: "Revision", time: "Authors: 30 days", desc: "Authors submit point-by-point response and revised manuscript." },
              { step: "6", label: "Publication", time: "1–5 days post-accept", desc: "Article assigned a permanent URL and goes live on acceptance." },
            ].map(({ step, label, time, desc }) => (
              <div key={step} style={{ background: "white", padding: "20px" }}>
                <div style={{ fontFamily: "var(--mono)", fontSize: "22px", fontWeight: 500, color: "var(--ink-15)", lineHeight: 1, marginBottom: "8px" }}>{step}</div>
                <div style={{ fontFamily: "var(--serif-display)", fontSize: "15px", fontWeight: 700, marginBottom: "4px" }}>{label}</div>
                <div style={{ fontFamily: "var(--mono)", fontSize: "9px", color: "var(--scarlet)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "8px" }}>{time}</div>
                <p style={{ fontSize: "13px", color: "var(--ink-70)", lineHeight: 1.7 }}>{desc}</p>
              </div>
            ))}
          </div>
          <p style={{ fontSize: "15px", color: "var(--ink-70)", lineHeight: "1.8" }}>Reviewers are selected based on subject expertise and declared absence of conflicts of interest. Authors may suggest or exclude up to three reviewers each. Editor decisions are final and not subject to appeal except in cases of clear procedural error.</p>
        </div>
      ),
    },
    {
      title: "Licensing & Copyright", icon: "07",
      content: (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px", fontSize: "15px", color: "var(--ink-70)", lineHeight: "1.8" }}>
          <p>ScienceXplorer operates on an <strong>open-access</strong> publishing model under Creative Commons CC-BY 4.0. To support sustainable operations, Article Processing Charges (APCs) are determined on a <strong>case-by-case basis</strong> upon acceptance. Full 100% fee waivers are strictly granted exclusively to authors from eligible low-income countries (see our dedicated <button onClick={() => navigate("apcwaiver")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--scarlet)", textDecoration: "underline", font: "inherit", padding: 0 }}>APC Waiver Policy</button>).</p>
          <div style={{ background: "var(--cream)", border: "1px solid var(--ink-15)", padding: "20px 24px", display: "flex", gap: "20px", alignItems: "flex-start" }}>
            <div style={{ fontFamily: "var(--serif-display)", fontSize: "32px", color: "var(--scarlet)", lineHeight: 1, flexShrink: 0 }}>©</div>
            <div>
              <strong style={{ fontFamily: "var(--serif-display)", fontSize: "16px", display: "block", marginBottom: "6px" }}>CC-BY 4.0 License</strong>
              <p style={{ fontSize: "14px" }}>Authors retain full copyright. All articles are published under the Creative Commons Attribution 4.0 International License, permitting unrestricted use, distribution, and reproduction in any medium, provided the original work is properly cited.</p>
            </div>
          </div>
          <p>Third-party material (figures, tables, extended quotations) reproduced from other sources must be accompanied by written permission from the copyright holder or evidence that the material is in the public domain or licensed under a compatible Creative Commons license.</p>
        </div>
      ),
    },
  ];
  return (
    <div>
      <div style={{ background: "var(--cream)", borderBottom: "1px solid var(--ink-15)", padding: "60px 0 56px" }}>
        <div className="container">
          <div className="label animate-up" style={{ color: "var(--ink-40)", marginBottom: "14px" }}>Editorial Policies</div>
          <h1 className="display-lg animate-up animate-up-d1" style={{ marginBottom: "14px" }}>Author Guidelines</h1>
          <p className="animate-up animate-up-d2" style={{ color: "var(--ink-70)", fontSize: "17px", maxWidth: "560px", lineHeight: 1.7 }}>Everything you need to prepare and submit a manuscript that meets ScienceXplorer standards.</p>
          <div className="animate-up animate-up-d3" style={{ display: "flex", gap: "12px", marginTop: "28px", flexWrap: "wrap" }}>
            <button onClick={() => navigate("submit")} className="btn btn-primary">Submit Now</button>
            <button onClick={() => navigate("editorial")} className="btn btn-ghost">Meet the Editors →</button>
          </div>
        </div>
      </div>
      <div style={{ background: "var(--ink)", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
        <div className="container" style={{ padding: "0 24px" }}>
          <div style={{ display: "flex", overflowX: "auto", WebkitOverflowScrolling: "touch", scrollbarWidth: "none" }}>
            {[
              { label: "Word Limit", value: "12,000" },
              { label: "Citation Style", value: "APA 7th" },
              { label: "Review Type", value: "Double-blind" },
              { label: "Decision Time", value: "72 hrs" },
              { label: "APC", value: "Case-by-case" },
              { label: "License", value: "CC-BY 4.0" },
            ].map(({ label, value }) => (
              <div key={label} style={{ padding: "16px 24px", borderRight: "1px solid rgba(255,255,255,0.1)", flexShrink: 0 }}>
                <div style={{ fontFamily: "var(--mono)", fontSize: "9px", color: "rgba(255,255,255,0.35)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "4px" }}>{label}</div>
                <div style={{ fontFamily: "var(--serif-display)", fontSize: "16px", fontWeight: 600, color: "white" }}>{value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="container" style={{ padding: "60px 24px 80px", maxWidth: "860px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          {sections.map((s, i) => {
            const isOpen = openSection === i;
            return (
              <div key={i} className="animate-up" style={{ animationDelay: `${i * 0.04}s`, background: "white", border: "1px solid var(--ink-15)", overflow: "hidden" }}>
                <button onClick={() => toggle(i)} style={{ width: "100%", padding: "22px 28px", background: isOpen ? "var(--ink)" : "white", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: "20px", transition: "background 0.2s" }}>
                  <span style={{ fontFamily: "var(--mono)", fontSize: "11px", color: isOpen ? "rgba(255,255,255,0.3)" : "var(--ink-15)", fontWeight: 500, flexShrink: 0, letterSpacing: "0.05em" }}>{s.icon}</span>
                  <span style={{ fontFamily: "var(--serif-display)", fontSize: "18px", fontWeight: 600, color: isOpen ? "white" : "var(--ink)", flex: 1, textAlign: "left" }}>{s.title}</span>
                  <span style={{ fontFamily: "var(--mono)", fontSize: "18px", color: isOpen ? "var(--scarlet)" : "var(--ink-40)", flexShrink: 0, transition: "transform 0.25s", transform: isOpen ? "rotate(45deg)" : "none", lineHeight: 1 }}>+</span>
                </button>
                <div style={{ maxHeight: isOpen ? "2000px" : "0", overflow: "hidden", transition: "max-height 0.4s cubic-bezier(0.4,0,0.2,1)" }}>
                  <div style={{ padding: "28px 28px 32px", borderTop: "1px solid var(--ink-07)" }}>{s.content}</div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="animate-up" style={{ background: "#F0FDF4", border: "1px solid #86EFAC", padding: "32px", marginTop: "40px" }}>
          <div className="label" style={{ color: "#14532D", marginBottom: "14px" }}>Pre-Submission Checklist</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "8px" }}>
            {[
              "Manuscript is anonymized (no author names in body or file metadata)",
              "Abstract is structured and under 300 words",
              "References follow APA 7th edition",
              "All figures are minimum 300 DPI",
              "Raw data deposited in public repository",
              "IRB/ethics approval included (if applicable)",
              "Competing interests declaration completed",
              "All authors meet ICMJE criteria",
              "Word count verified (excluding references)",
              "Keywords: 4–8 terms, not duplicating title",
            ].map((item) => (
              <div key={item} style={{ display: "flex", gap: "10px", alignItems: "flex-start", fontSize: "13px", color: "#166534", lineHeight: 1.6 }}>
                <span style={{ color: "#16A34A", flexShrink: 0, marginTop: "2px" }}>✓</span>{item}
              </div>
            ))}
          </div>
        </div>
        <div className="animate-up" style={{ marginTop: "32px", padding: "24px 28px", border: "1px solid var(--ink-15)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px" }}>
          <div>
            <div style={{ fontFamily: "var(--serif-display)", fontSize: "17px", fontWeight: 600, marginBottom: "4px" }}>Questions about submission requirements?</div>
            <p style={{ fontSize: "14px", color: "var(--ink-70)" }}>Our editorial office responds within one business day.</p>
          </div>
          <button onClick={() => navigate("submit")} className="btn btn-primary btn-sm">Contact Editorial Office</button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   FOOTER (unchanged)
   ───────────────────────────────────────────────────────────────────────── */
function Footer({ navigate }) {
  return (
    <footer style={{ borderTop: "1px solid var(--ink-15)", background: "var(--ink)", marginTop: "60px" }}>
      <div className="container" style={{ padding: "48px 24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "32px" }}>
          <div>
            <button onClick={() => navigate("home")} style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "var(--serif-display)", fontSize: "20px", fontWeight: 900, color: "white", letterSpacing: "-0.02em", padding: 0, display: "flex", alignItems: "baseline", gap: "1px" }}>
              Science<span style={{ color: "var(--scarlet)", fontStyle: "italic" }}>X</span>plorer
            </button>
            <p style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "rgba(255,255,255,0.3)", letterSpacing: "0.05em", marginTop: "6px" }}>OPEN ACCESS · PEER-REVIEWED · CC-BY 4.0</p>
          </div>
          <div style={{ display: "flex", gap: "32px", flexWrap: "wrap" }}>
            {[
              { label: "Front Page", key: "home" },
              { label: "Archive", key: "archive" },
              { label: "Author Index", key: "authors" },
              { label: "About", key: "about" },
              { label: "Editorial Board", key: "editorial" },
              { label: "Author Guidelines", key: "guidelines" },
              { label: "APC", key: "apc" },
              { label: "APC Waiver", key: "apcwaiver" },
              { label: "Submit", key: "submit" },
            ].map((l) => (
              <button key={l.key} onClick={() => navigate(l.key)} style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "var(--mono)", fontSize: "10px", letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.4)", transition: "color 0.2s" }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "white")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.4)")}>
                {l.label}
              </button>
            ))}
          </div>
        </div>
        <div className="rule" style={{ margin: "28px 0", background: "rgba(255,255,255,0.1)" }} />
        <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "8px" }}>
          <span style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "rgba(255,255,255,0.25)", letterSpacing: "0.05em" }}>© 2026 ScienceXplorer Press</span>
          <span style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "rgba(255,255,255,0.25)", letterSpacing: "0.05em" }}>OPEN-ACCESS RESEARCH JOURNAL</span>
        </div>
      </div>
    </footer>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   ROOT APP – handles /papers redirection and all pages
   ───────────────────────────────────────────────────────────────────────── */
export default function App() {
  const { route, navigate } = useRouter();
  const { page, id } = route;

  // Handle /papers/:filename – redirect to Supabase
  if (page === "papers" && id) {
    const supabaseUrl = getSupabasePDFUrl(id);
    window.location.href = supabaseUrl;
    return null;
  }

  // Admin page
  if (page === "admin") {
    return (
      <>
        <GlobalStyles />
        <AdminPageWrapper navigate={navigate} />
      </>
    );
  }

  // Manuscript Formatter (Admin Only Production Tool)
  if (page === "formatter") {
    return (
      <>
        <GlobalStyles />
        <AdminFormatterWrapper navigate={navigate} />
      </>
    );
  }

  // Normal app
  return (
    <>
      <GlobalStyles />
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        <Nav page={page} navigate={navigate} />
        <main style={{ flex: 1 }}>
          {page === "home" && <HomePage navigate={navigate} />}
          {page === "article" && <ArticlePage id={id} navigate={navigate} />}
          {(page === "authors" || page === "author") && (
            <AuthorIndexPage
              navigate={navigate}
              targetAuthor={id}
              fetchArticlesFn={fetchArticles}
            />
          )}
          {page === "submit" && <SubmitPage navigate={navigate} />}
          {page === "archive" && <ArchivePage navigate={navigate} />}
          {page === "about" && <AboutPage navigate={navigate} />}
          {page === "editorial" && <EditorialBoardPage navigate={navigate} />}
          {page === "guidelines" && <GuidelinesPage navigate={navigate} />}
          {page === "apc" && <APCPage navigate={navigate} />}
          {page === "apcwaiver" && <APCWaiverPage navigate={navigate} />}
        </main>
        <Footer navigate={navigate} />
      </div>
    </>
  );
}